// Maintenance tab panel (issue #143, Stage 1): powerplant baseline setup +
// timeline foundation.
//
// Gating: the real page renders only when the server-confirmed
// maintenance_enabled app-setting is on (via /api/app-config). While off,
// this module leaves the existing preview + "Coming Soon" treatment exactly
// as production shows it today — including on direct #maintenance navigation.
// Server APIs are gated independently (they 404 while off), so UI gating here
// is presentation, not security.
//
// Layout (decided 2026-07-03 with mockups): inline baseline setup section,
// then KPI rollup cards over the timeline once the baseline is complete.
// Rollups are computed from baseline + entries — no manual overrides in v1.

import {
  BORESCOPE_CONDITION_TAGS,
  BORESCOPE_MAX_IMAGE_BYTES,
  BORESCOPE_VALVE_TAGS,
  MAGNETO_POSITIONS,
  PROPELLER_WORK_TYPES,
  PROPELLER_WORK_TYPE_LABELS,
  SPARK_PLUG_ACTIONS,
  SPARK_PLUG_ACTION_LABELS,
  computeMagnetoStatus,
  computePropellerStatus,
  computeSparkPlugLayout,
  computeSparkPlugUsage,
  rotateSparkPlugLayout,
  sparkPlugPositions,
  MAINTENANCE_ATTACHMENT_MAX_BYTES,
  MAINTENANCE_IMAGE_SELECT_MAX_BYTES,
  MAINTENANCE_DELETE_RETENTION_DAYS,
  MAINTENANCE_ENTRY_KINDS,
  MAINTENANCE_ENTRY_KIND_LABELS,
  MAINTENANCE_HOUR_SOURCES,
  MAINTENANCE_HOUR_SOURCE_LABELS,
  MAINTENANCE_COMPRESSION_OVER,
  computeMaintenanceOilStatus,
  computeMaintenanceOilUsage,
  MAINTENANCE_OIL_RECENT_INTERVALS,
  computeMaintenanceRollups,
  latestCompressionForEngine,
  maintenanceBaselineRequiredGaps,
  maintenanceBaselineSeedFromProfile,
  maintenanceDeleteWarningCopy,
  maintenanceHourTriangle,
  normalizeMaintenanceBaselineJson,
  normalizeMaintenanceEntryKind,
  normalizeMaintenanceHourSource,
} from "../core/maintenance.js";
import type { MaintenanceBaselineJson, MaintenanceHourSource } from "../core/maintenance.js";

type MaintenancePanelDeps = {
  escapeHtml: (value: unknown) => string;
  appReadOnlyMode: () => boolean;
  maintenanceFeatureEnabled: () => boolean;
  getAircraftProfiles: () => Record<string, any> | null | undefined;
  aircraftDisplayLabel: (aircraft: string) => string;
  aircraftMakeModelLabel: (profile: any) => string;
  onMaintenanceChanged?: (aircraftId: string) => void;
};

type PanelState = {
  aircraft: string | null;
  aircraftId: string | null;
  loadKey: number;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  baselineRow: any;
  entries: any[];
  editingBaseline: boolean;
  selectedEngineId: string;
  systemsOpen: boolean;
  expandedSystems: string[];
  historyQuery: string;
  historyKind: string;
  compactOil: boolean;
  savingEntry: boolean;
  savingBaseline: boolean;
  formDirty: boolean;
  activeOperations: number;
  returnAction: string;
  composerOpen: boolean;
  composerKind: string;
  composerPlugAction: string;
  composerPlugMode: string;
  editingEntryId: string | null;
  pendingAttachments: File[];
  status: { message: string; isError: boolean } | null;
  borescope: {
    loaded: boolean;
    loading: boolean;
    inspections: any[];
    editingInspectionId: string | null;
    selectedImageId: string | null;
    creating: boolean;
    createDraft: { inspectedOn: string; engineId: string | null; note: string } | null;
    noteDraft: string | null;
    imageDrafts: Record<string, { valveTag: string | null; note: string; conditionTags: string[] }>;
    saving: boolean;
    returnToActivity: boolean;
  };
};

function freshState(): PanelState {
  return {
    aircraft: null,
    aircraftId: null,
    loadKey: 0,
    loading: false,
    loaded: false,
    error: null,
    baselineRow: null,
    entries: [],
    editingBaseline: false,
    selectedEngineId: "",
    systemsOpen: false,
    expandedSystems: [],
    historyQuery: "",
    historyKind: "",
    compactOil: false,
    savingEntry: false,
    savingBaseline: false,
    formDirty: false,
    activeOperations: 0,
    returnAction: "open-composer",
    composerOpen: false,
    composerKind: "general",
    composerPlugAction: "clean",
    composerPlugMode: "top-bottom-advance",
    editingEntryId: null,
    pendingAttachments: [],
    status: null,
    borescope: {
      loaded: false,
      loading: false,
      inspections: [],
      editingInspectionId: null,
      selectedImageId: null,
      creating: false,
      createDraft: null,
      noteDraft: null,
      imageDrafts: {},
      saving: false,
      returnToActivity: false,
    },
  };
}

function attachmentIcon(contentType: string | null | undefined): string {
  return contentType === "application/pdf" ? "📄" : "🖼";
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatOilQuantity(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(Number(value))
    ? "Unknown" : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatOilRate(value: number | null | undefined): string {
  return value === null || value === undefined ? "Not available" : `${value.toFixed(3)} qt/hr`;
}

function maintenanceDateLabel(dateText: string | null | undefined): string {
  const text = String(dateText ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "—";
  // Noon-UTC keeps the calendar day stable in every local timezone (same
  // convention as flight_date handling).
  const parsed = new Date(`${text}T12:00:00Z`);
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function purgeDateLabel(deletedAt: string | null | undefined): string {
  const dayText = String(deletedAt ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayText)) return "";
  const parsed = new Date(`${dayText}T12:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + MAINTENANCE_DELETE_RETENTION_DAYS);
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export function createAircraftMaintenancePanel({
  escapeHtml,
  appReadOnlyMode,
  maintenanceFeatureEnabled,
  getAircraftProfiles,
  aircraftDisplayLabel,
  aircraftMakeModelLabel,
  onMaintenanceChanged = () => {},
}: MaintenancePanelDeps) {
  let state = freshState();
  let wired = false;

  const contentEl = (): HTMLElement | null => document.querySelector("#aircraftMaintenanceContent");
  const panelEl = (): HTMLElement | null => document.querySelector("#aircraftMaintenancePanel");

  function currentProfile(): any {
    return state.aircraft ? getAircraftProfiles()?.[state.aircraft] ?? null : null;
  }

  function engineDisplayName(engine: any, index: number): string {
    return engine?.label || (engineCount() > 1 ? `Engine ${index + 1}` : "Engine");
  }

  function engineCount(): number {
    return state.baselineRow?.baseline?.engines?.length
      ?? draftBaseline()?.engines?.length
      ?? 1;
  }

  function draftBaseline(): MaintenanceBaselineJson {
    if (state.baselineRow?.baseline) return state.baselineRow.baseline;
    return normalizeMaintenanceBaselineJson(maintenanceBaselineSeedFromProfile(currentProfile()));
  }

  function draftHourSource(): MaintenanceHourSource {
    return normalizeMaintenanceHourSource(state.baselineRow?.hourSource ?? "tach");
  }

  function baselineGaps(): string[] {
    return maintenanceBaselineRequiredGaps(state.baselineRow?.baseline ?? null, draftHourSource());
  }

  async function maintenanceFetch(path: string, options: RequestInit = {}): Promise<any> {
    const operationState = state;
    const writing = Boolean(options.method && options.method !== "GET");
    if (writing) operationState.activeOperations++;
    try {
      const aircraftId = state.aircraftId ?? "";
      const response = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/maintenance${path}`, {
        cache: "no-store",
        ...options,
        headers: { "content-type": "application/json", ...(options.headers ?? {}) },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Maintenance API HTTP ${response.status}`);
      }
      if (options.method && options.method !== "GET") onMaintenanceChanged(aircraftId);
      return payload;
    } finally { if (writing) operationState.activeOperations--; }
  }

  async function loadMaintenance() {
    const loadState = state;
    const loadKey = ++state.loadKey;
    state.loading = true;
    state.error = null;
    try {
      const payload = await maintenanceFetch("");
      if (state !== loadState || loadKey !== state.loadKey) return;
      state.baselineRow = payload?.baseline ?? null;
      state.entries = Array.isArray(payload?.entries) ? payload.entries : [];
      state.loaded = true;
    } catch (error: any) {
      if (state !== loadState || loadKey !== state.loadKey) return;
      state.error = error?.message ?? "Could not load maintenance records.";
    } finally {
      if (state === loadState && loadKey === state.loadKey) {
        state.loading = false;
        renderContent();
      }
    }
  }

  function setStatus(message: string | null, { isError = false } = {}) {
    state.status = message ? { message, isError } : null;
  }

  // ---------------------------------------------------------------- markup --

  function noticeMarkup(message: string, { tone = "info" }: { tone?: "info" | "error" } = {}): string {
    return `<div class="maintenance-live-notice${tone === "error" ? " is-error" : ""}">${escapeHtml(message)}</div>`;
  }

  // Always renders the element (empty when idle) so paintStatus() can update
  // it in place without a full re-render — full re-renders wipe typed form
  // values, and mid-upload progress shouldn't churn the DOM.
  function statusMarkup(): string {
    return `<p class="report-status${state.status ? ` is-active${state.status.isError ? " is-error" : " is-success"}` : ""}" data-mx-status role="status">${state.status ? escapeHtml(state.status.message) : ""}</p>`;
  }

  function paintStatus(): boolean {
    const el = contentEl()?.querySelector("[data-mx-status]") as HTMLElement | null;
    if (!el) return false;
    el.className = `report-status${state.status ? ` is-active${state.status.isError ? " is-error" : " is-success"}` : ""}`;
    el.textContent = state.status?.message ?? "";
    if (state.status) el.scrollIntoView({ block: "nearest" });
    return true;
  }

  function fileSizeLabel(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Selection-time size gate: oversized files never upload; the rejection is
  // shown immediately instead of a server 413 landing below the fold.
  function splitOversizedFiles(files: File[], maxBytes: number): { accepted: File[]; rejectedMessage: string | null } {
    const accepted = files.filter((file) => file.size <= maxBytes);
    const rejected = files.filter((file) => file.size > maxBytes);
    if (!rejected.length) return { accepted, rejectedMessage: null };
    const names = rejected.map((file) => `“${file.name}” (${fileSizeLabel(file.size)})`).join(", ");
    return {
      accepted,
      rejectedMessage: `${names} ${rejected.length === 1 ? "is" : "are"} over the ${fileSizeLabel(maxBytes)} limit and ${rejected.length === 1 ? "was" : "were"} not uploaded.`,
    };
  }

  // Camera files may be picked up to 20 MB; anything past the trigger gets
  // downscaled in the browser (2560 px long edge, JPEG) so the stored size
  // stays respectable. Detail at 2560 px comfortably covers borescope review.
  const IMAGE_DOWNSCALE_TRIGGER_BYTES = 2.5 * 1024 * 1024;
  const IMAGE_DOWNSCALE_MAX_EDGE = 2560;

  type PreparedUpload = { body: Blob; fileName: string; contentType: string };

  async function downscaleImage(file: File): Promise<PreparedUpload | null> {
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, IMAGE_DOWNSCALE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
      if (!blob) return null;
      if (blob.size >= file.size) return { body: file, fileName: file.name, contentType: file.type || "application/octet-stream" };
      return { body: blob, fileName: `${file.name.replace(/\.[^.]+$/, "")}.jpg`, contentType: "image/jpeg" };
    } catch {
      // Undecodable in this browser (HEIC outside Safari, corrupt file).
      return null;
    }
  }

  async function prepareImageForUpload(file: File, storedCapBytes: number): Promise<PreparedUpload | { error: string }> {
    if (file.size <= Math.min(IMAGE_DOWNSCALE_TRIGGER_BYTES, storedCapBytes)) {
      return { body: file, fileName: file.name, contentType: file.type || "application/octet-stream" };
    }
    const downscaled = await downscaleImage(file);
    if (downscaled && downscaled.body.size <= storedCapBytes) return downscaled;
    if (!downscaled && file.size <= storedCapBytes) {
      return { body: file, fileName: file.name, contentType: file.type || "application/octet-stream" };
    }
    return {
      error: downscaled
        ? `“${file.name}” couldn't be reduced under ${fileSizeLabel(storedCapBytes)} and was not uploaded.`
        : `“${file.name}” (${fileSizeLabel(file.size)}) can't be converted in this browser — export it as JPEG under ${fileSizeLabel(storedCapBytes)} and try again.`,
    };
  }

  function hoursFieldMarkup(label: string, attr: string, value: number | null, { required = false, placeholder = "hrs" } = {}): string {
    return `
      <label class="upload-field">
        <span>${escapeHtml(label)}${required ? ' <em class="maintenance-required" title="Required before entries unlock">*</em>' : ""}</span>
        <input type="number" step="0.1" min="0" inputmode="decimal" placeholder="${escapeHtml(placeholder)}" ${attr} value="${value ?? ""}" />
      </label>
    `;
  }

  function dateFieldMarkup(label: string, attr: string, value: string | null): string {
    return `
      <label class="upload-field">
        <span>${escapeHtml(label)}</span>
        <input type="date" ${attr} value="${escapeHtml(value ?? "")}" />
      </label>
    `;
  }

  function textFieldMarkup(label: string, attr: string, value: string | null, { placeholder = "" } = {}): string {
    return `
      <label class="upload-field">
        <span>${escapeHtml(label)}</span>
        <input type="text" maxlength="120" placeholder="${escapeHtml(placeholder)}" ${attr} value="${escapeHtml(value ?? "")}" />
      </label>
    `;
  }

  function setupFormMarkup(): string {
    const baseline = draftBaseline();
    const hourSource = draftHourSource();
    const profile = currentProfile();
    const readOnly = appReadOnlyMode();
    const multiEngine = baseline.engines.length > 1;

    const engineSections = baseline.engines.map((engine, index) => `
      <div class="form-section-block" data-mx-engine-block="${index}">
        ${multiEngine ? `<h4 class="maintenance-engine-heading">${escapeHtml(engineDisplayName(engine, index))}</h4>` : ""}
        <div class="aircraft-form-grid aircraft-form-grid-four">
          ${hoursFieldMarkup("Engine total time", `data-mx-eng="${index}:engineTotalTimeHours"`, engine.engineTotalTimeHours, { required: true })}
          ${hoursFieldMarkup("TSMOH", `data-mx-eng="${index}:tsmohHours"`, engine.tsmohHours, { required: true })}
        </div>
        <p class="maintenance-form-hint">TSMOH is time since major overhaul. Enter both values at the baseline meter reading below.</p>
        <details class="maintenance-setup-optional"><summary>Engine identity and overhaul history <span>Optional</span></summary>
          <div class="aircraft-form-grid aircraft-form-grid-four">
            ${textFieldMarkup("Engine make", `data-mx-eng="${index}:engineMake"`, engine.engineMake)}
            ${textFieldMarkup("Engine model", `data-mx-eng="${index}:engineModel"`, engine.engineModel)}
            <label class="upload-field"><span>Cylinders</span><input type="number" step="1" min="1" max="16" inputmode="numeric" data-mx-eng="${index}:cylinderCount" value="${engine.cylinderCount ?? ""}" /></label>
            ${dateFieldMarkup("Overhaul date", `data-mx-eng="${index}:overhaulDate"`, engine.overhaulDate)}
            ${hoursFieldMarkup("Tach at overhaul", `data-mx-eng="${index}:tachAtOverhaulHours"`, engine.tachAtOverhaulHours)}
            ${hoursFieldMarkup("TTAF at overhaul", `data-mx-eng="${index}:ttafAtOverhaulHours"`, engine.ttafAtOverhaulHours)}
          </div>
        </details>
      </div>
    `).join("");

    const propSections = baseline.propellers.map((prop, index) => `
      <div class="form-section-block" data-mx-prop-block="${index}">
        ${multiEngine ? `<h4 class="maintenance-engine-heading">${escapeHtml(engineDisplayName(baseline.engines.find((engine) => engine.engineId === prop.engineId) ?? prop, index))} propeller</h4>` : ""}
        <div class="aircraft-form-grid aircraft-form-grid-four">
          ${textFieldMarkup("Prop make", `data-mx-prop="${index}:propMake"`, prop.propMake, { placeholder: "e.g. McCauley" })}
          ${textFieldMarkup("Prop model", `data-mx-prop="${index}:propModel"`, prop.propModel)}
          ${hoursFieldMarkup("Prop total time", `data-mx-prop="${index}:propTotalTimeHours"`, prop.propTotalTimeHours)}
          ${hoursFieldMarkup("Prop since overhaul", `data-mx-prop="${index}:propSinceOverhaulHours"`, prop.propSinceOverhaulHours)}
          ${dateFieldMarkup("Prop overhaul date", `data-mx-prop="${index}:propOverhaulDate"`, prop.propOverhaulDate)}
          ${dateFieldMarkup("Prop install date", `data-mx-prop="${index}:propInstallDate"`, prop.propInstallDate)}
          ${hoursFieldMarkup("Prop total time at install", `data-mx-prop="${index}:propInstallTimeHours"`, prop.propInstallTimeHours)}
        </div>
      </div>
    `).join("");

    const sourceOptions = MAINTENANCE_HOUR_SOURCES.map((source) => `
      <option value="${source}" ${source === hourSource ? "selected" : ""}>${MAINTENANCE_HOUR_SOURCE_LABELS[source]}${source === "tach" ? " (default)" : ""}</option>
    `).join("");

    return `
      <section class="panel maintenance-live-panel" data-mx-setup-panel>
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Maintenance</p>
            <h2>Powerplant baseline</h2>
          </div>
          <div class="maintenance-header-actions">
            ${state.baselineRow ? '<button class="secondary-action" type="button" data-mx-action="cancel-baseline">Cancel</button>' : ""}
            <button class="primary-action" type="button" data-mx-action="save-baseline"${readOnly ? " disabled" : ""}>${maintenanceBaselineRequiredGaps(baseline, hourSource).length ? "Save draft" : "Save baseline"}</button>
          </div>
        </div>
        <form class="maintenance-baseline-form" data-mx-baseline-form novalidate>
          <div class="maintenance-setup-readiness" data-mx-setup-readiness role="status"></div>
          <p class="maintenance-form-hint">${escapeHtml(state.aircraft ? aircraftDisplayLabel(state.aircraft) : "")} / ${escapeHtml(aircraftMakeModelLabel(profile))}. Aircraft identity is managed in Aircraft settings.</p>
          <section class="aircraft-form-section">
            <h3>Engine${multiEngine ? "s" : ""}</h3>
            ${engineSections}
          </section>
          <section class="aircraft-form-section">
            <h3>Baseline meters</h3>
            <div class="aircraft-form-grid aircraft-form-grid-four" data-mx-required-meters>
              ${hoursFieldMarkup(`Tach now`, 'data-mx-hours="tachHours"', baseline.hours.tachHours, { required: hourSource === "tach" })}
              ${hoursFieldMarkup(`Hobbs now`, 'data-mx-hours="hobbsHours"', baseline.hours.hobbsHours, { required: hourSource === "hobbs" })}
              ${hoursFieldMarkup(`TTAF now`, 'data-mx-hours="ttafHours"', baseline.hours.ttafHours, { required: true })}
              <label class="upload-field">
                <span>Tally source</span>
                <select data-mx-source>${sourceOptions}</select>
              </label>
            </div>
            <p class="maintenance-form-hint">The tally source drives elapsed time. TTAF is total time on the airframe. Use Update hours in Activity for later readings.</p>
            <details class="maintenance-setup-optional"><summary>Other meter readings <span>Optional</span></summary><div class="aircraft-form-grid aircraft-form-grid-four" data-mx-other-meters></div></details>
          </section>
          <details class="aircraft-form-section maintenance-setup-optional" data-mx-setup-propellers>
            <summary>Propeller${baseline.propellers.length > 1 ? "s" : ""} <span>Optional equipment history</span></summary>
            ${propSections}
          </details>
          <details class="aircraft-form-section maintenance-setup-optional">
            <summary>Oil change tracking <span>Optional interval and starting reading</span></summary>
            <div class="aircraft-form-grid aircraft-form-grid-four">
              ${hoursFieldMarkup("Oil change interval", 'data-mx-oil="changeIntervalHours"', baseline.oil?.changeIntervalHours ?? null, { placeholder: "hrs · e.g. 50" })}
              ${hoursFieldMarkup(`Last oil change ${MAINTENANCE_HOUR_SOURCE_LABELS[hourSource]}`, 'data-mx-oil="lastChangeHours"', baseline.oil?.lastChangeHours ?? null, { placeholder: "hrs · if known" })}
            </div>
            <p class="maintenance-form-hint">Until an oil entry is logged, "since change" counts from the last-change reading above.</p>
          </details>
          <details class="aircraft-form-section maintenance-setup-optional">
            <summary>Ignition <span>Optional equipment history</span></summary>
            ${baseline.engines.map((engine, engineIndex) => `
              <div class="form-section-block">
                ${multiEngine ? `<h4 class="maintenance-engine-heading">${escapeHtml(engineDisplayName(engine, engineIndex))}</h4>` : ""}
                ${MAGNETO_POSITIONS.map((position) => {
                  const mag = baseline.ignition?.magnetos?.find((item) => item.engineId === engine.engineId && item.position === position);
                  return `
                    <div class="aircraft-form-grid aircraft-form-grid-four">
                      ${textFieldMarkup(`${position} mag part #`, `data-mx-mag="${engineIndex}:${position}:partNumber"`, mag?.partNumber ?? null, { placeholder: "e.g. 10-500556-101" })}
                      ${textFieldMarkup("Serial #", `data-mx-mag="${engineIndex}:${position}:serialNumber"`, mag?.serialNumber ?? null)}
                      ${dateFieldMarkup("Install date", `data-mx-mag="${engineIndex}:${position}:installDate"`, mag?.installDate ?? null)}
                      ${hoursFieldMarkup("Time since overhaul", `data-mx-mag="${engineIndex}:${position}:tsmohHours"`, mag?.tsmohHours ?? null)}
                    </div>
                  `;
                }).join("")}
              </div>
            `).join("")}
            <div class="aircraft-form-grid aircraft-form-grid-four">
              ${hoursFieldMarkup("Mag inspection interval", 'data-mx-ign="magInspectionIntervalHours"', baseline.ignition?.magInspectionIntervalHours ?? null, { placeholder: "hrs · e.g. 500" })}
            </div>
            <p class="maintenance-form-hint">Mag time since overhaul advances on the tally source; timing checks reset the inspection countdown.</p>
          </details>
          ${statusMarkup()}
        </form>
      </section>
    `;
  }

  function kpiCardsMarkup(): string {
    const baseline = state.baselineRow?.baseline;
    const rollups = computeMaintenanceRollups(baseline, draftHourSource(), state.entries);
    if (!rollups) return "";
    const multiEngine = rollups.engines.length > 1;
    const engineCards = rollups.engines.map((engine, index) => `
      <article class="maintenance-kpi-card">
        <div class="maintenance-kpi-icon">◊</div>
        <div>
          ${multiEngine || engine.engineModel ? `<span class="maintenance-engine-chip">${escapeHtml([multiEngine ? engineDisplayName(engine, index) : "Engine", engine.engineModel].filter(Boolean).join(" · "))}</span>` : ""}
          <p>Time since major overhaul</p>
          <strong>${formatHours(engine.tsmohHours)} <span>hrs</span></strong>
          <small>Engine TT ${formatHours(engine.engineTotalTimeHours)}${engine.overhaulDate ? ` · OH ${escapeHtml(maintenanceDateLabel(engine.overhaulDate))}` : ""}</small>
        </div>
      </article>
    `).join("");
    const propCards = rollups.propellers.map((prop, index) => `
      <article class="maintenance-kpi-card">
        <div class="maintenance-kpi-icon is-purple">✣</div>
        <div>
          ${multiEngine || prop.propModel ? `<span class="maintenance-engine-chip">${escapeHtml([multiEngine ? engineDisplayName(rollups.engines.find((engine) => engine.engineId === prop.engineId) ?? prop, index) : "Propeller", prop.propModel].filter(Boolean).join(" · "))}</span>` : ""}
          <p>Prop since overhaul</p>
          <strong>${formatHours(prop.propSinceOverhaulHours)} <span>hrs</span></strong>
          <small>Prop TT ${formatHours(prop.propTotalTimeHours)}${prop.propOverhaulDate ? ` · OH ${escapeHtml(maintenanceDateLabel(prop.propOverhaulDate))}` : ""}</small>
        </div>
      </article>
    `).join("");
    const oilStatus = computeMaintenanceOilStatus(baseline, draftHourSource(), state.entries);
    // Whiteboard framing: the headline is the countdown ("due in N hrs" /
    // "overdue by N hrs"), and the meter drains toward the change.
    const oilCards = (oilStatus?.engines ?? []).map((oil, index) => {
      const tone = oil.state === "overdue" ? "is-red" : oil.state === "due-soon" ? "is-amber" : oil.state === "ok" ? "is-green" : "";
      const pill = oil.state === "overdue" ? "Overdue" : oil.state === "due-soon" ? "Due soon" : oil.state === "ok" ? "On track" : "";
      // The meter drains toward the change; once overdue it flips to a full
      // red bar (a drained-empty red bar would be invisible).
      const remainingWidth = oil.state === "overdue"
        ? 100
        : oil.percentOfInterval === null
          ? 0
          : Math.max(0, 100 - Math.min(100, oil.percentOfInterval));
      const heading = oil.state === "overdue" ? "Oil change overdue by" : "Oil change due in";
      const headline = oil.state === "no-data"
        ? "—"
        : formatHours(oil.state === "overdue" ? Math.abs(oil.dueInHours ?? 0) : oil.dueInHours);
      const caption = oil.state === "no-data"
        ? oil.lastChangeOccurredOn ? `Add or correct the ${MAINTENANCE_HOUR_SOURCE_LABELS[draftHourSource()]} readings to track the interval` : "Log an oil change to start tracking"
        : `${formatHours(oil.sinceChangeHours)} hrs since change${oil.lastChangeOccurredOn ? ` · ${escapeHtml(maintenanceDateLabel(oil.lastChangeOccurredOn))}` : oil.lastChangeHours !== null ? ` · from baseline at ${formatHours(oil.lastChangeHours)}` : ""}`;
      return `
      <article class="maintenance-kpi-card">
        <div class="maintenance-kpi-icon ${tone === "is-red" ? "is-oil-red" : tone === "is-amber" ? "is-oil-amber" : ""}">◊</div>
        <div class="maintenance-kpi-main">
          ${multiEngine ? `<span class="maintenance-engine-chip">${escapeHtml(engineDisplayName(rollups.engines[index] ?? oil, index))}</span>` : ""}
          <p>${oil.state === "no-data" ? "Oil change" : heading}</p>
          <strong class="${tone}">${headline} <span>hrs</span></strong>
          <small>${caption}</small>
          <div class="maintenance-meter ${tone === "is-red" ? "is-overdue" : tone === "is-amber" ? "is-due-soon" : ""}"><span style="width: ${remainingWidth}%"></span></div>
        </div>
        <div class="maintenance-kpi-side">
          <small>Interval</small>
          <strong>${formatHours(oilStatus?.intervalHours ?? null)} hrs</strong>
          ${pill ? `<span class="maintenance-status ${tone}">${pill}</span>` : ""}
        </div>
      </article>
    `;
    }).join("");
    const sourceLabel = MAINTENANCE_HOUR_SOURCE_LABELS[rollups.hourSource];
    return `
      <div class="maintenance-kpi-grid maintenance-live-kpis">
        ${engineCards}
        ${propCards}
        ${oilCards}
        <article class="maintenance-kpi-card">
          <div class="maintenance-kpi-icon is-blue">◷</div>
          <div>
            <p>Aircraft hours</p>
            <strong>${formatHours(rollups.currentHours.ttafHours)} <span>TTAF</span></strong>
            ${(["ttaf", "tach", "hobbs"] as const).map(source => {
              const meter = meterContext(source);
              return `<small class="maintenance-meter-reading">${escapeHtml(meter.label)} ${formatHours(meter.value)}${meter.value == null ? " (not recorded)" : ` h · ${meter.date ? escapeHtml(maintenanceDateLabel(meter.date)) : "date unavailable"}`}</small>`;
            }).join("")}
            <small class="maintenance-meter-reading">Recorded values; dates may differ.</small>
          </div>
        </article>
        <article class="maintenance-kpi-card">
          <div class="maintenance-kpi-icon">⚖</div>
          <div>
            <p>Tally source</p>
            <strong>${escapeHtml(sourceLabel)}</strong>
            <small>Rollups advance on ${escapeHtml(sourceLabel.toLowerCase())} entries${meterContext().date ? ` · as of ${escapeHtml(maintenanceDateLabel(meterContext().date))}` : " · reading date unavailable"}</small>
          </div>
        </article>
      </div>
    `;
  }

  function baselineSummaryMarkup(): string {
    const row = state.baselineRow;
    if (!row?.baseline) return "";
    const readOnly = appReadOnlyMode();
    const baseline = row.baseline as MaintenanceBaselineJson;
    const engine = baseline.engines[0];
    const summaryBits = [
      [engine?.engineMake, engine?.engineModel].filter(Boolean).join(" "),
      engine?.cylinderCount ? `${engine.cylinderCount} cyl` : "",
      baseline.propellers.map((prop) => [prop.propMake, prop.propModel].filter(Boolean).join(" ")).filter(Boolean).join(", "),
    ].filter(Boolean).join(" · ");
    const hoursBits = `${MAINTENANCE_HOUR_SOURCE_LABELS[draftHourSource()]} ${formatHours(baseline.hours[draftHourSource() === "tach" ? "tachHours" : draftHourSource() === "hobbs" ? "hobbsHours" : "ttafHours"])}, TTAF ${formatHours(baseline.hours.ttafHours)}`;
    return `
      <section class="panel maintenance-live-panel">
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Baseline</p>
            <h2>Powerplant baseline</h2>
          </div>
          <button class="secondary-action" type="button" data-mx-action="edit-baseline"${readOnly ? " disabled" : ""}>Edit baseline</button>
        </div>
        <p class="maintenance-baseline-summary">
          ${escapeHtml(summaryBits || "Powerplant recorded")} · Baseline at ${escapeHtml(hoursBits)}${row.updatedAt ? ` · Updated ${escapeHtml(maintenanceDateLabel(String(row.updatedAt)))}` : ""}
        </p>
      </section>
    `;
  }

  // The chip list re-renders in place on file selection/removal so typed
  // composer values survive (a full renderContent would wipe them).
  function attachListMarkup(savedAttachments: any[]): string {
    return `
      ${savedAttachments.map((attachment: any) => `
        <span class="maintenance-attach-chip">
          ${attachmentIcon(attachment.contentType)} ${escapeHtml(attachment.fileName)}
          <button class="maintenance-attach-remove" type="button" data-mx-action="delete-attachment" data-mx-attachment-id="${escapeHtml(attachment.id)}" title="Delete attachment" aria-label="Delete ${escapeHtml(attachment.fileName)}">✕</button>
        </span>
      `).join("")}
      ${state.pendingAttachments.map((file, index) => `
        <span class="maintenance-attach-chip is-pending">
          ${attachmentIcon(file.type)} ${escapeHtml(file.name)}
          <button class="maintenance-attach-remove" type="button" data-mx-action="remove-pending-attachment" data-mx-attach-index="${index}" title="Remove" aria-label="Remove ${escapeHtml(file.name)}">✕</button>
        </span>
      `).join("")}
      <button class="secondary-action maintenance-attach-add" type="button" data-mx-action="pick-attachment" aria-label="Attach report or photo">
        + Attach report or photo
      </button>
      <input type="file" accept="application/pdf,image/jpeg,image/png" multiple hidden data-mx-attach-input />
    `;
  }

  function refreshAttachList() {
    const list = contentEl()?.querySelector("[data-mx-attach-list]") as HTMLElement | null;
    if (!list) return;
    const restoreFocus = list.contains(document.activeElement);
    const editing = state.editingEntryId
      ? state.entries.find((entry) => entry.id === state.editingEntryId) ?? null
      : null;
    list.innerHTML = attachListMarkup(Array.isArray(editing?.attachments) ? editing.attachments : []);
    bindFileInputs();
    if (restoreFocus) list.querySelector<HTMLButtonElement>('[data-mx-action="pick-attachment"]')?.focus();
  }

  // Cylinders section (Stage 3, issues #145/#141): latest compression
  // readings per engine, rendered neutrally — values are records, never
  // colored, interpreted, or advised on.
  function compressionModuleMarkup(): string {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    if (!baseline) return "";
    const readOnly = appReadOnlyMode();
    const multiEngine = baseline.engines.length > 1;
    const engineBlocks = baseline.engines.map((engine, index) => {
      const latest = latestCompressionForEngine(state.entries, engine.engineId) as any;
      const compression = latest?.details?.compression ?? null;
      const cylinders = engine.cylinderCount ?? compression?.readings?.length ?? 0;
      if (!cylinders) return "";
      const cells = Array.from({ length: Math.max(cylinders, compression?.readings?.length ?? 0) }, (_, cyl) => `
        <div class="maintenance-comp-cell">
          <small>Cyl ${cyl + 1}</small>
          <strong>${compression?.readings?.[cyl] ?? "—"}<span>/${compression?.over ?? MAINTENANCE_COMPRESSION_OVER}</span></strong>
        </div>
      `).join("");
      const meta = latest
        ? `Latest check <b>${escapeHtml(maintenanceDateLabel(latest.occurredOn))}</b> · <b>${compression?.condition === "cold" ? "Cold" : "Hot"}</b>${latest.tachHours !== null && latest.tachHours !== undefined ? ` · Tach <b>${formatHours(latest.tachHours)}</b>` : ""}${latest.notes ? ` · “${escapeHtml(latest.notes)}”` : ""}. Find earlier readings using Compression history above.`
        : "No compression check logged yet.";
      return `
        <div class="maintenance-comp-engine">
          ${multiEngine ? `<span class="maintenance-engine-chip">${escapeHtml(engineDisplayName(engine, index))}</span>` : ""}
          <div class="maintenance-comp-grid">${cells}</div>
          <p class="maintenance-comp-meta">${meta}</p>
        </div>
      `;
    }).join("");
    if (!engineBlocks.trim()) return "";
    return `
      <section class="panel maintenance-live-panel">
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Cylinders</p>
            <h2>Compression</h2>
          </div>
          <button class="primary-action" type="button" data-mx-action="open-compression-composer"${readOnly ? " disabled" : ""}>+ Log compression check</button>
        </div>
        <div class="maintenance-comp-body">${engineBlocks}</div>
      </section>
    `;
  }

  // ------------------------------------------------------------ borescope --
  // Image library per issues #145/#141: inspections grouped by date and
  // same-day sequence, cylinder slots from the inspection's snapshot count.
  // Recordkeeping only — tags and notes describe, never diagnose.

  const borescopeThumbUrls = new Map<string, string>();

  function releaseBorescopeThumbs() {
    for (const url of borescopeThumbUrls.values()) URL.revokeObjectURL(url);
    borescopeThumbUrls.clear();
  }

  async function borescopeFetch(path: string, options: RequestInit = {}): Promise<any> {
    const operationState = state;
    const writing = Boolean(options.method && options.method !== "GET");
    if (writing) operationState.activeOperations++;
    try {
      const aircraftId = state.aircraftId ?? "";
      const response = await fetch(`/api/aircraft/${encodeURIComponent(aircraftId)}/borescope${path}`, {
        cache: "no-store",
        ...options,
        headers: { "content-type": "application/json", ...(options.headers ?? {}) },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || `Borescope API HTTP ${response.status}`);
      if (options.method && options.method !== "GET") onMaintenanceChanged(aircraftId);
      return payload;
    } finally { if (writing) operationState.activeOperations--; }
  }

  async function loadBorescope() {
    if (state.borescope.loading) return;
    const loadState = state;
    state.borescope.loading = true;
    try {
      const payload = await borescopeFetch("");
      if (state !== loadState) return;
      state.borescope.inspections = Array.isArray(payload?.inspections) ? payload.inspections : [];
      state.borescope.loaded = true;
    } catch (error: any) {
      if (state !== loadState) return;
      setStatus(error?.message ?? "Could not load borescope inspections.", { isError: true });
    } finally {
      if (state !== loadState) return;
      state.borescope.loading = false;
      if (!state.composerOpen && !state.editingEntryId) renderContent();
    }
  }

  async function refreshBorescope() {
    const loadState = state;
    const payload = await borescopeFetch("");
    if (state !== loadState) return;
    state.borescope.inspections = Array.isArray(payload?.inspections) ? payload.inspections : state.borescope.inspections;
  }

  async function generateBorescopeThumbnail(file: File): Promise<Blob | null> {
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 256 / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, width, height);
      return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));
    } catch {
      // HEIC (outside Safari) and corrupt files land here — the original still
      // uploads; the tile shows a labeled placeholder instead of a thumbnail.
      return null;
    }
  }

  async function uploadBorescopeImages(inspectionId: string, cylinder: number, files: File[], { keepStatus = false } = {}) {
    const operationState = state;
    operationState.activeOperations++;
    try {
      let skippedMessage: string | null = null;
      for (const file of files) {
        // Progress paints in place; a full re-render here would detach the
        // other cylinders' file inputs mid-flow.
        setStatus(`Uploading ${file.name}…`);
        if (!paintStatus()) renderContent();
        try {
          const prepared = await prepareImageForUpload(file, BORESCOPE_MAX_IMAGE_BYTES);
          if ("error" in prepared) {
            skippedMessage = prepared.error;
            setStatus(skippedMessage, { isError: true });
            if (!paintStatus()) renderContent();
            continue;
          }
          const buffer = await prepared.body.arrayBuffer();
          const payload = await borescopeFetch(`/${encodeURIComponent(inspectionId)}/images?cylinder=${cylinder}&filename=${encodeURIComponent(prepared.fileName)}`, {
            method: "POST",
            headers: { "content-type": prepared.contentType },
            body: buffer,
          });
          const imageId = payload?.image?.id;
          if (imageId) {
            const thumb = await generateBorescopeThumbnail(file);
            if (thumb) {
              await borescopeFetch(`/images/${encodeURIComponent(imageId)}/thumbnail`, {
                method: "PUT",
                headers: { "content-type": "image/jpeg" },
                body: await thumb.arrayBuffer(),
              });
            }
          }
        } catch (error: any) {
          setStatus(`“${file.name}” did not upload: ${error?.message ?? "unknown error"}`, { isError: true });
          await refreshBorescope();
          renderContent();
          paintStatus();
          return;
        }
      }
      if (skippedMessage) {
        setStatus(skippedMessage, { isError: true });
      } else if (!keepStatus) {
        setStatus(null);
      }
      await refreshBorescope();
      renderContent();
      if (keepStatus || skippedMessage) paintStatus();
    } finally { operationState.activeOperations--; }
  }

  function hydrateBorescopeThumbs() {
    const content = contentEl();
    if (!content) return;
    content.querySelectorAll("[data-bs-thumb]").forEach(async (img) => {
      const loadState = state;
      const imageId = (img as HTMLImageElement).dataset.bsThumb ?? "";
      if (!imageId) return;
      const cached = borescopeThumbUrls.get(imageId);
      if (cached) {
        (img as HTMLImageElement).src = cached;
        return;
      }
      try {
        const response = await fetch(`/api/aircraft/${encodeURIComponent(state.aircraftId ?? "")}/borescope/images/${encodeURIComponent(imageId)}?thumb=1`, { cache: "no-store" });
        if (!response.ok) throw new Error(String(response.status));
        const blob = await response.blob();
        if (state !== loadState) return;
        const url = URL.createObjectURL(blob);
        borescopeThumbUrls.set(imageId, url);
        (img as HTMLImageElement).src = url;
      } catch {
        (img as HTMLImageElement).closest(".maintenance-bs-thumb")?.classList.add("is-unavailable");
      }
    });
  }

  async function openBorescopeImage(imageId: string) {
    try {
      const response = await fetch(`/api/aircraft/${encodeURIComponent(state.aircraftId ?? "")}/borescope/images/${encodeURIComponent(imageId)}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`Image HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      setStatus(error?.message ?? "Could not open the image.", { isError: true });
      renderContent();
    }
  }

  function borescopeInspectionById(inspectionId: string | null): any {
    return inspectionId ? state.borescope.inspections.find((item) => item.id === inspectionId) ?? null : null;
  }

  function borescopeThumbMarkup(image: any): string {
    const heicNoThumb = !image.hasThumbnail && image.contentType === "image/heic";
    return `
      <button class="maintenance-bs-thumb${heicNoThumb ? " is-heic" : ""}" type="button" data-mx-action="bs-open-image" data-mx-image-id="${escapeHtml(image.id)}" title="${escapeHtml(image.fileName)}">
        ${heicNoThumb ? "<span>HEIC<br>download to view</span>" : `<img data-bs-thumb="${escapeHtml(image.id)}" alt="${escapeHtml(image.fileName)}" />`}
        ${image.conditionTags?.length ? '<i class="maintenance-bs-flag"></i>' : ""}
      </button>
    `;
  }

  function borescopeCylinderSlots(inspection: any, { editable = false }: { editable?: boolean } = {}): string {
    const readOnly = appReadOnlyMode();
    const byCylinder = new Map<number, any[]>();
    for (const image of inspection.images ?? []) {
      const list = byCylinder.get(image.cylinderNumber) ?? [];
      list.push(image);
      byCylinder.set(image.cylinderNumber, list);
    }
    const maxCylinder = Math.max(inspection.cylinderCount, ...Array.from(byCylinder.keys(), (n) => Number(n)), 1);
    return `
      <div class="maintenance-bs-cyl-row">
        ${Array.from({ length: maxCylinder }, (_, index) => {
          const cylinder = index + 1;
          const images = byCylinder.get(cylinder) ?? [];
          const canAdd = editable && !readOnly && images.length < 5;
          return `
            <div class="maintenance-bs-cyl">
              <div class="maintenance-bs-cyl-label"><small>Cyl ${cylinder}</small><span>${editable ? `${images.length} / 5` : images.length || ""}</span></div>
              <div class="maintenance-bs-thumbs">
                ${images.map((image) => borescopeThumbMarkup(image)).join("")}
                ${!images.length && !editable ? '<div class="maintenance-bs-empty">No images</div>' : ""}
                ${canAdd ? `
                  <button class="maintenance-bs-empty is-add" type="button" data-mx-action="bs-pick-images" aria-label="Add image to cylinder ${cylinder}">
                    + Add image
                  </button>
                  <input type="file" accept="image/jpeg,image/png,.heic,image/heic" multiple hidden data-bs-upload="${cylinder}" />
                ` : ""}
              </div>
            </div>
          `;
        }).join("")}
      </div>
    `;
  }

  function borescopeDirty(): boolean {
    const inspection = borescopeInspectionById(state.borescope.editingInspectionId);
    return state.borescope.noteDraft !== null && state.borescope.noteDraft !== (inspection?.note ?? "")
      || Object.keys(state.borescope.imageDrafts).length > 0;
  }

  function paintBorescopeDraftStatus() {
    const notice = contentEl()?.querySelector<HTMLElement>("[data-bs-draft-status]");
    if (notice) notice.textContent = state.borescope.saving ? "Saving changes..." : borescopeDirty() ? "Unsaved changes" : "All changes saved";
  }

  function captureBorescopeImageDraft() {
    const detail = contentEl()?.querySelector("[data-bs-detail]");
    const id = state.borescope.selectedImageId;
    if (!id || !detail) return;
    state.borescope.imageDrafts[id] = {
      valveTag: detail.querySelector<HTMLSelectElement>("[data-bs-detail-valve]")?.value || null,
      note: detail.querySelector<HTMLInputElement>("[data-bs-detail-note]")?.value ?? "",
      conditionTags: Array.from(detail.querySelectorAll<HTMLElement>("[data-bs-tag].is-active")).map(tag => tag.dataset.bsTag!),
    };
    const saved = borescopeInspectionById(state.borescope.editingInspectionId)?.images?.find((image: any) => image.id === id);
    const draft = state.borescope.imageDrafts[id];
    if (saved && draft.note === (saved.note ?? "") && draft.valveTag === (saved.valveTag ?? null)
      && JSON.stringify([...draft.conditionTags].sort()) === JSON.stringify([...(saved.conditionTags ?? [])].sort())) delete state.borescope.imageDrafts[id];
    paintBorescopeDraftStatus();
  }

  async function saveBorescopeDrafts(imageId?: string) {
    const inspection = borescopeInspectionById(state.borescope.editingInspectionId);
    if (!inspection || appReadOnlyMode() || state.borescope.saving) return;
    state.borescope.saving = true;
    setStatus("Saving changes...");
    renderContent();
    try {
      const imageIds = imageId ? [imageId] : Object.keys(state.borescope.imageDrafts);
      for (const id of imageIds) {
        const draft = state.borescope.imageDrafts[id];
        if (!draft) continue;
        await borescopeFetch(`/images/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(draft) });
        const image = inspection.images?.find((item: any) => item.id === id);
        if (image) Object.assign(image, draft);
        delete state.borescope.imageDrafts[id];
      }
      if (!imageId && state.borescope.noteDraft !== null) {
        const note = state.borescope.noteDraft;
        await borescopeFetch(`/${encodeURIComponent(inspection.id)}`, { method: "PUT", body: JSON.stringify({ note }) });
        inspection.note = note;
        state.borescope.noteDraft = null;
      }
      setStatus(imageId ? "Image details saved." : "Inspection changes saved.");
    } catch (error: any) {
      setStatus(error?.message ?? "Could not save changes. Your edits are still here.", { isError: true });
    } finally {
      state.borescope.saving = false;
      renderContent();
      contentEl()?.querySelector<HTMLElement>(imageId ? '[data-mx-action="bs-save-image"]' : '[data-mx-action="bs-save-inspection"]')?.focus();
    }
  }

  function borescopeEditorMarkup(inspection: any): string {
    const readOnly = appReadOnlyMode();
    const savedImage = state.borescope.selectedImageId
      ? (inspection.images ?? []).find((image: any) => image.id === state.borescope.selectedImageId) ?? null
      : null;
    const selected = savedImage ? { ...savedImage, ...state.borescope.imageDrafts[savedImage.id] } : null;
    return `
      <section class="panel maintenance-live-panel" data-bs-editor>
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Borescope · ${escapeHtml(maintenanceDateLabel(inspection.inspectedOn))} · Inspection ${inspection.sequence}</p>
            <h2>${readOnly ? "Inspection" : "Edit inspection"}</h2>
          </div>
          <div class="maintenance-header-actions">
            <button class="secondary-action danger-action" type="button" data-mx-action="bs-delete-inspection" data-mx-inspection-id="${escapeHtml(inspection.id)}"${readOnly || state.borescope.saving ? " disabled" : ""}>Delete inspection</button>
            <button class="secondary-action" type="button" data-mx-action="bs-close-editor"${state.borescope.saving ? " disabled" : ""}>Close</button>
            <button class="primary-action" type="button" data-mx-action="bs-save-inspection"${readOnly || state.borescope.saving ? " disabled" : ""}>Save inspection</button>
          </div>
        </div>
        <div class="panel-body">
          <p role="status" data-bs-draft-status>${state.borescope.saving ? "Saving changes..." : borescopeDirty() ? "Unsaved changes" : "All changes saved"}</p>
          <p class="maintenance-form-hint">Save inspection saves the note and all edited image details. Images are saved when uploaded. Cylinders without images remain visible as a partial inspection.</p>
          <fieldset class="maintenance-bs-fields" ${readOnly || state.borescope.saving ? "disabled" : ""}>
          <label class="upload-field">
            <span>Inspection note</span>
            <input type="text" maxlength="4000" data-bs-inspection-note value="${escapeHtml(state.borescope.noteDraft ?? inspection.note ?? "")}" />
          </label>
          ${borescopeCylinderSlots(inspection, { editable: true })}
          ${selected ? `
            <div class="maintenance-bs-detail" data-bs-detail>
              <span class="maintenance-oil-tag">Selected image — Cyl ${selected.cylinderNumber} · ${escapeHtml(selected.fileName)}</span>
              <div class="aircraft-form-grid aircraft-form-grid-four">
                <label class="upload-field">
                  <span>Valve</span>
                  <select data-bs-detail-valve>
                    <option value="">Untagged</option>
                    ${BORESCOPE_VALVE_TAGS.map((tag) => `<option value="${tag}" ${selected.valveTag === tag ? "selected" : ""}>${tag[0].toUpperCase()}${tag.slice(1)}</option>`).join("")}
                  </select>
                </label>
                <label class="upload-field aircraft-form-span-2">
                  <span>Image note</span>
                  <input type="text" maxlength="2000" data-bs-detail-note value="${escapeHtml(selected.note ?? "")}" />
                </label>
              </div>
              <div class="maintenance-bs-tags">
                ${BORESCOPE_CONDITION_TAGS.map((tag) => `
                  <button class="maintenance-bs-tag${selected.conditionTags?.includes(tag) ? " is-active" : ""}" type="button" data-mx-action="bs-toggle-tag" aria-pressed="${selected.conditionTags?.includes(tag) ? "true" : "false"}" data-bs-tag="${escapeHtml(tag)}">${escapeHtml(tag)}</button>
                `).join("")}
              </div>
              <div class="maintenance-composer-actions">
                <button class="secondary-action danger-action" type="button" data-mx-action="bs-delete-image" data-mx-image-id="${escapeHtml(selected.id)}"${readOnly || state.borescope.saving ? " disabled" : ""}>Delete image</button>
                <button class="primary-action" type="button" data-mx-action="bs-save-image" data-mx-image-id="${escapeHtml(selected.id)}"${readOnly || state.borescope.saving ? " disabled" : ""}>Save image details</button>
              </div>
            </div>
          ` : '<p class="maintenance-form-hint">Select a thumbnail to tag it or add an image note.</p>'}
          </fieldset>
          ${statusMarkup()}
        </div>
      </section>
    `;
  }

  // ------------------------------------------------------------- ignition --
  // Stage 4 (issue #146): magneto identity/timing + spark plug board.

  function plugCellMarkup(position: string, label: string | null, { after = false }: { after?: boolean } = {}): string {
    return `
      <div class="maintenance-plug-cell${after ? " is-after" : ""}">
        <small>${escapeHtml(position)}</small>
        <b>${label ? escapeHtml(label) : "—"}</b>
      </div>
    `;
  }

  function plugGridMarkup(layout: Record<string, string | null>, cylinderCount: number, { after = false }: { after?: boolean } = {}): string {
    const count = Math.max(1, cylinderCount);
    const row = (rowKey: "T" | "B") => Array.from({ length: count }, (_, index) =>
      plugCellMarkup(`${index + 1}${rowKey}`, layout[`${index + 1}${rowKey}`] ?? null, { after })).join("");
    return `
      <div class="maintenance-plug-grid" style="--plug-cols: ${count};">
        <div class="maintenance-plug-row"><small>Top</small>${row("T")}</div>
        <div class="maintenance-plug-row"><small>Bottom</small>${row("B")}</div>
      </div>
    `;
  }

  function magnetosModuleMarkup(): string {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    if (!baseline) return "";
    const status = computeMagnetoStatus(baseline, draftHourSource(), state.entries);
    if (!status) return "";
    const readOnly = appReadOnlyMode();
    const multiEngine = baseline.engines.length > 1;
    const hasIdentity = status.engines.some((engine) => engine.mags.some((mag) =>
      mag.partNumber || mag.serialNumber || mag.installDate || mag.tsmohHours !== null));
    if (!hasIdentity && status.intervalHours === null) return "";
    const engineBlocks = status.engines.map((engine, index) => {
      const magCards = engine.mags.map((mag) => `
        <article class="maintenance-kpi-card maintenance-mag-card">
          <div>
            <span class="maintenance-engine-chip">${escapeHtml(`${multiEngine ? `${engineDisplayName(engine, index)} · ` : ""}${mag.position} mag`)}</span>
            <div class="maintenance-mag-lines">
              <span>P/N <b>${escapeHtml(mag.partNumber ?? "—")}</b> · S/N <b>${escapeHtml(mag.serialNumber ?? "—")}</b></span>
              <span>Installed <b>${escapeHtml(mag.installDate ? maintenanceDateLabel(mag.installDate) : "—")}</b> · TSMOH <b>${formatHours(mag.currentTsmohHours)}${mag.currentTsmohHours !== null ? " hrs" : ""}</b></span>
              <span>${mag.lastTiming
                ? `Last timing <b>${escapeHtml(maintenanceDateLabel(mag.lastTiming.occurredOn))}</b> · measured <b>${mag.lastTiming.measuredDeg ?? "—"}°</b> · set <b>${mag.lastTiming.setDeg ?? "—"}°</b>`
                : "No timing check logged yet."}</span>
            </div>
          </div>
        </article>
      `).join("");
      let dueCard = "";
      if (engine.due) {
        const tone = engine.due.state === "overdue" ? "is-red" : engine.due.state === "due-soon" ? "is-amber" : engine.due.state === "ok" ? "is-green" : "";
        const pill = engine.due.state === "overdue" ? "Overdue" : engine.due.state === "due-soon" ? "Due soon" : engine.due.state === "ok" ? "On track" : "";
        const remaining = engine.due.state === "overdue" ? 100
          : engine.due.percentOfInterval === null ? 0 : Math.max(0, 100 - engine.due.percentOfInterval);
        dueCard = `
          <article class="maintenance-kpi-card">
            <div class="maintenance-kpi-main">
              ${multiEngine ? `<span class="maintenance-engine-chip">${escapeHtml(engineDisplayName(engine, index))}</span>` : ""}
              <p>${engine.due.state === "overdue" ? "Mag inspection overdue by" : "Mag inspection due in"}</p>
              <strong class="${tone}">${engine.due.state === "no-data" ? "—" : formatHours(engine.due.state === "overdue" ? Math.abs(engine.due.dueInHours ?? 0) : engine.due.dueInHours)} <span>hrs</span></strong>
              <small>${engine.due.state === "no-data" ? "Log a timing check to start the countdown" : `${formatHours(engine.due.sinceHours)} hrs since last check${engine.due.lastCheckOn ? ` · ${escapeHtml(maintenanceDateLabel(engine.due.lastCheckOn))}` : ""}`}</small>
              <div class="maintenance-meter ${tone === "is-red" ? "is-overdue" : tone === "is-amber" ? "is-due-soon" : ""}"><span style="width: ${remaining}%"></span></div>
            </div>
            <div class="maintenance-kpi-side">
              <small>Interval</small>
              <strong>${formatHours(status.intervalHours)} hrs</strong>
              ${pill ? `<span class="maintenance-status ${tone}">${pill}</span>` : ""}
            </div>
          </article>
        `;
      }
      return `${magCards}${dueCard}`;
    }).join("");
    return `
      <section class="panel maintenance-live-panel">
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Ignition</p>
            <h2>Magnetos</h2>
          </div>
          <button class="primary-action" type="button" data-mx-action="open-mag-timing"${readOnly ? " disabled" : ""}>+ Log timing check</button>
        </div>
        <div class="maintenance-comp-body">
          <div class="maintenance-kpi-grid maintenance-mag-grid">${engineBlocks}</div>
        </div>
      </section>
    `;
  }

  function sparkPlugModuleMarkup(): string {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    if (!baseline) return "";
    const readOnly = appReadOnlyMode();
    const multiEngine = baseline.engines.length > 1;
    const engineBlocks = baseline.engines.map((engine, index) => {
      const cylinders = engine.cylinderCount ?? 0;
      if (!cylinders) return "";
      const applicable = state.entries.filter(entry => entry.engineId || !multiEngine);
      const { layout, lastService } = computeSparkPlugLayout(applicable, engine.engineId, cylinders);
      const usage = computeSparkPlugUsage(baseline, state.baselineRow?.hourSource ?? "tach", state.entries, engine.engineId, cylinders);
      const hours = (value: number) => `${value.toFixed(1)} h`;
      return `
        <div class="maintenance-comp-engine">
          ${multiEngine ? `<span class="maintenance-engine-chip">${escapeHtml(engineDisplayName(engine, index))}</span>` : ""}
          ${plugGridMarkup(layout, cylinders)}
          <p class="maintenance-comp-meta">${lastService
            ? `Last service <b>${escapeHtml(maintenanceDateLabel(lastService.occurredOn))}</b>: ${escapeHtml(SPARK_PLUG_ACTION_LABELS[lastService.action])}.`
            : "No plug service logged yet."} Hours follow the plug ID through rotations, using recorded ${escapeHtml(MAINTENANCE_HOUR_SOURCE_LABELS[state.baselineRow?.hourSource ?? "tach"])} readings. T = top, B = bottom.</p>
          ${usage.length ? `<div class="maintenance-plug-usage">${usage.map(plug => `
            <details data-mx-plug-usage="${escapeHtml(plug.id)}">
              <summary><strong>${escapeHtml(plug.id)}</strong><span>${plug.currentPosition ? `Position ${escapeHtml(plug.currentPosition)}` : "Removed"}</span><span>${plug.totalHours === null ? `${hours(plug.recordedHours)} recorded; total unknown` : `${hours(plug.totalHours)} total`}</span></summary>
              <p class="maintenance-form-hint">Hours entered at identification: ${plug.priorHours === null ? "unknown" : hours(plug.priorHours)}. ${plug.incomplete ? "Some intervals have missing or inconsistent readings; recorded hours cover only known intervals." : "Position hours cover the recorded placement history only."}</p>
              <dl class="maintenance-plug-position-hours">${Object.entries(plug.positions).map(([position, use]) => `<div><dt>Cylinder ${Number.parseInt(position)} ${position.endsWith("T") ? "top" : "bottom"}</dt><dd>${hours(use.hours)}${use.incomplete ? " recorded (incomplete)" : ""}</dd></div>`).join("")}</dl>
            </details>`).join("")}</div>` : '<p class="maintenance-form-hint">Use Track plug hours to identify installed plugs. Enter prior hours if known, or leave them blank. Existing history is not assumed to start with new plugs.</p>'}

        </div>
      `;
    }).join("");
    if (!engineBlocks.trim()) return "";
    return `
      <section class="panel maintenance-live-panel" data-mx-plugs-panel>
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Ignition</p>
            <h2>Spark plugs</h2>
          </div>
          <div class="maintenance-header-actions">
            <button class="secondary-action" type="button" data-mx-action="open-plug-track"${readOnly ? " disabled" : ""}>Track plug hours</button>
            <button class="secondary-action" type="button" data-mx-action="open-plug-service"${readOnly ? " disabled" : ""}>+ Plug service</button>
            <button class="primary-action" type="button" data-mx-action="open-plug-rotate"${readOnly ? " disabled" : ""}>Rotate plugs</button>
          </div>
        </div>
        <div class="maintenance-comp-body">${engineBlocks}</div>
      </section>
    `;
  }

  function oilModuleMarkup(): string {
    const usage = computeMaintenanceOilUsage(state.baselineRow?.baseline, draftHourSource(), state.entries);
    if (!usage) return "";
    const readOnly = appReadOnlyMode();
    const engineCards = usage.engines.map((engine, index) => {
      const current = engine.current;
      const recent = engine.recentAverage;
      const intervals = engine.recentIntervals.map(interval => `
        <li>${escapeHtml(maintenanceDateLabel(interval.startOn))} to ${escapeHtml(maintenanceDateLabel(interval.endOn))}:
          ${formatOilQuantity(interval.addedQuarts)} qt over ${formatHours(interval.elapsedHours)} hrs,
          ${formatOilRate(interval.quartsPerHour)}${interval.incomplete ? " (incomplete records)" : ""}</li>
      `).join("");
      return `
        <article class="maintenance-kpi-card maintenance-mag-card" data-mx-oil-engine="${escapeHtml(engine.engineId)}">
          <div>
            <span class="maintenance-engine-chip">${escapeHtml(engineDisplayName(engine, index))}</span>
            <div class="maintenance-mag-lines">
              ${current ? `
                <span>Last oil change <b>${escapeHtml(maintenanceDateLabel(current.startOn))}</b></span>
                <span>Since change <b data-mx-oil-elapsed>${current.elapsedHours === null ? "Unknown" : `${formatHours(current.elapsedHours)} hrs`}</b></span>
                <span>Logged top-ups <b data-mx-oil-total>${formatOilQuantity(current.addedQuarts)} qt</b> (${current.topUpCount} record${current.topUpCount === 1 ? "" : "s"})</span>
                <span>Top-up rate <b data-mx-oil-rate>${formatOilRate(current.quartsPerHour)}</b></span>
                ${current.incomplete ? '<span>Complete the quantities, engine assignments, and meter readings for this interval to calculate a rate.</span>' : current.elapsedHours === 0 ? '<span>A later meter reading is needed to calculate a rate.</span>' : ""}
              ` : '<span>Log an oil change to group top-ups into an interval.</span>'}
              <span>Recent average <b data-mx-oil-average>${formatOilRate(recent?.quartsPerHour)}</b></span>
              <span>${recent ? `${recent.intervalCount} of ${engine.recentIntervals.length} recent completed interval${engine.recentIntervals.length === 1 ? "" : "s"}, ${formatOilQuantity(recent.addedQuarts)} qt / ${formatHours(recent.elapsedHours)} hrs` : "No completed intervals with enough data yet."}</span>
              ${engine.ungroupedTopUpCount ? `<span>${engine.ungroupedTopUpCount} top-up record${engine.ungroupedTopUpCount === 1 ? " is" : "s are"} before the first logged oil change and excluded from rates.</span>` : ""}
            </div>
            ${intervals ? `<details><summary>Recent oil intervals</summary><ul>${intervals}</ul></details>` : ""}
          </div>
        </article>
      `;
    }).join("");
    return `
      <section class="panel maintenance-live-panel" data-mx-oil-section>
        <div class="panel-header compact-header">
          <div><p class="eyebrow">Oil</p><h2>Oil history</h2></div>
          <div class="maintenance-toolbar">
            <button class="secondary-action" type="button" data-mx-action="open-oil-change"${readOnly ? " disabled" : ""}>+ Oil change</button>
            <button class="primary-action" type="button" data-mx-action="open-oil-added"${readOnly ? " disabled" : ""}>+ Add oil</button>
          </div>
        </div>
        <div class="maintenance-comp-body">
          <div class="maintenance-kpi-grid maintenance-mag-grid">${engineCards}</div>
          <p class="maintenance-form-hint">Rates use logged top-ups and ${MAINTENANCE_HOUR_SOURCE_LABELS[usage.hourSource]} hours, excluding refill oil at changes. Missing top-up records understate consumption. The recent average divides total top-up quarts by hours across usable intervals among the last ${MAINTENANCE_OIL_RECENT_INTERVALS} completed intervals.</p>
        </div>
      </section>
    `;
  }

  // ----------------------------------------------------------- propeller --
  // Stage 5 (issue #147): per-propeller identity + rollups + work history.
  function propellerModuleMarkup(): string {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    if (!baseline) return "";
    const status = computePropellerStatus(baseline, draftHourSource(), state.entries);
    if (!status) return "";
    const readOnly = appReadOnlyMode();
    const multiEngine = baseline.engines.length > 1;
    const engineLabelFor = (engineId: string) => {
      const index = baseline.engines.findIndex((engine) => engine.engineId === engineId);
      return index >= 0 ? engineDisplayName(baseline.engines[index], index) : null;
    };
    const propBlocks = status.propellers.map((prop) => {
      const chipLabel = [multiEngine ? engineLabelFor(prop.engineId) : null, prop.propModel].filter(Boolean).join(" · ") || "Propeller";
      const identity = [
        [prop.propMake, prop.propModel].filter(Boolean).join(" "),
        prop.installDate ? `installed ${maintenanceDateLabel(prop.installDate)}` : "",
        prop.installTimeHours !== null ? `${formatHours(prop.installTimeHours)} hrs at install` : "",
      ].filter(Boolean).join(" · ");
      return `
        <article class="maintenance-kpi-card maintenance-mag-card">
          <div>
            <span class="maintenance-engine-chip">${escapeHtml(chipLabel)}</span>
            <div class="maintenance-mag-lines">
              <span>Since overhaul <b>${formatHours(prop.sinceOverhaulHours)}${prop.sinceOverhaulHours !== null ? " hrs" : ""}</b>${prop.overhaulDate ? ` · OH ${escapeHtml(maintenanceDateLabel(prop.overhaulDate))}` : ""}</span>
              <span>Total time <b>${formatHours(prop.totalTimeHours)}${prop.totalTimeHours !== null ? " hrs" : ""}</b>${identity ? ` · ${escapeHtml(identity)}` : ""}</span>
              <span>${prop.lastWork
                ? `Last work <b>${escapeHtml(maintenanceDateLabel(prop.lastWork.occurredOn))}</b> · <b>${escapeHtml(PROPELLER_WORK_TYPE_LABELS[prop.lastWork.workType])}</b>`
                : "No propeller work logged yet."}</span>
            </div>
          </div>
        </article>
      `;
    }).join("");
    return `
      <section class="panel maintenance-live-panel">
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Propeller</p>
            <h2>Propeller${status.propellers.length > 1 ? "s" : ""}</h2>
          </div>
          <button class="primary-action" type="button" data-mx-action="open-propeller"${readOnly ? " disabled" : ""}>+ Log prop work</button>
        </div>
        <p class="maintenance-form-hint">Identity and installation history below come from Maintenance Setup. Aircraft profile equipment details are maintained separately. If they differ, review both records before updating the baseline.</p>
        <div class="maintenance-comp-body">
          <div class="maintenance-kpi-grid maintenance-mag-grid">${propBlocks}</div>
        </div>
      </section>
    `;
  }

  function borescopeModuleMarkup(): string {
    if (!state.baselineRow) return "";
    const readOnly = appReadOnlyMode();
    const editing = borescopeInspectionById(state.borescope.editingInspectionId);
    if (editing && editing.status === "active") return borescopeEditorMarkup(editing);
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    const engines = baseline?.engines ?? [];
    const groups = new Map<string, any[]>();
    for (const inspection of state.borescope.inspections) {
      const list = groups.get(inspection.inspectedOn) ?? [];
      list.push(inspection);
      groups.set(inspection.inspectedOn, list);
    }
    const gallery = state.borescope.inspections.length
      ? Array.from(groups.entries()).map(([date, inspections]) => `
        <div class="maintenance-bs-date-group">
          <div class="maintenance-bs-date-head"><h3>${escapeHtml(maintenanceDateLabel(date))}</h3><small>${inspections.length === 1 ? "1 inspection" : `${inspections.length} inspections`}</small></div>
          ${inspections.map((inspection) => inspection.status === "deleted" ? `
            <article class="maintenance-entry is-deleted">
              <div class="maintenance-entry-main">
                <h4>Inspection ${inspection.sequence}</h4>
                <p>${escapeHtml(maintenanceDeleteWarningCopy())}</p>
              </div>
              <div class="maintenance-entry-meta">
                <span class="maintenance-status is-amber">Deletes ${escapeHtml(purgeDateLabel(inspection.deletedAt))}</span>
                <button class="secondary-action maintenance-entry-action" type="button" data-mx-action="bs-restore-inspection" data-mx-inspection-id="${escapeHtml(inspection.id)}"${readOnly ? " disabled" : ""}>Restore</button>
              </div>
            </article>
          ` : `
            <article class="maintenance-bs-inspection">
              <div class="maintenance-bs-insp-head">
                <div class="maintenance-bs-insp-title">
                  <h4>Inspection ${inspection.sequence}</h4>
                  <span class="maintenance-engine-chip">${inspection.cylinderCount} cylinders</span>
                </div>
                <button class="secondary-action" type="button" data-mx-action="bs-open-inspection" data-mx-inspection-id="${escapeHtml(inspection.id)}">Open</button>
              </div>
              ${inspection.note ? `<p class="maintenance-comp-meta">“${escapeHtml(inspection.note)}”</p>` : ""}
              ${borescopeCylinderSlots(inspection)}
            </article>
          `).join("")}
        </div>
      `).join("")
      : '<p class="maintenance-timeline-empty">No borescope inspections yet. Create the first one to start the image library.</p>';
    const createRow = state.borescope.creating ? `
      <div class="maintenance-bs-create" data-bs-create>
        <div class="aircraft-form-grid aircraft-form-grid-four">
          <label class="upload-field"><span>Inspection date</span><input type="date" data-bs-create-date value="${escapeHtml(state.borescope.createDraft?.inspectedOn ?? todayIsoDate())}" /></label>
          ${engines.length > 1 ? `
            <label class="upload-field"><span>Engine</span>
              <select data-bs-create-engine>
                ${engines.map((engine, index) => `<option value="${escapeHtml(engine.engineId)}" ${state.borescope.createDraft?.engineId === engine.engineId ? "selected" : ""}>${escapeHtml(engineDisplayName(engine, index))}</option>`).join("")}
              </select>
            </label>
          ` : ""}
          <label class="upload-field aircraft-form-span-2"><span>Note</span><input type="text" maxlength="4000" placeholder="optional" value="${escapeHtml(state.borescope.createDraft?.note ?? "")}" data-bs-create-note /></label>
        </div>
        <div class="maintenance-composer-actions">
          <button class="secondary-action" type="button" data-mx-action="bs-cancel-create">Cancel</button>
          <button class="primary-action" type="button" data-mx-action="bs-create-inspection">Create inspection</button>
        </div>
      </div>
    ` : "";
    return `
      <section class="panel maintenance-live-panel">
        <div class="panel-header compact-header">
          <div>
            <p class="eyebrow">Cylinders</p>
            <h2>Borescope inspections</h2>
          </div>
          ${state.borescope.creating ? "" : `<button class="primary-action" type="button" data-mx-action="bs-new-inspection"${readOnly ? " disabled" : ""}>+ New borescope inspection</button>`}
        </div>
        <div class="panel-body">
          ${createRow}
          ${state.borescope.loading && !state.borescope.loaded ? '<p class="maintenance-form-hint">Loading inspections…</p>' : gallery}
        </div>
      </section>
    `;
  }

  // The composer's spark-plug body depends on the selected engine's board;
  // read the live select when present so twins compose against the right one.
  function composerEngineContext(): { engineId: string; cylinders: number } {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    const engines = baseline?.engines ?? [];
    const select = contentEl()?.querySelector('[data-mx-entry="engineId"]') as HTMLSelectElement | null;
    const engine = engines.find((item) => item.engineId === (select?.value || "")) ?? engines[0];
    return { engineId: engine?.engineId ?? "engine1", cylinders: engine?.cylinderCount ?? 4 };
  }

  // The board used inside the composer. On an aircraft where no plug has ever been
  // labeled the stored board is entirely empty, which makes a rotation preview a no-op
  // (rotating "—" yields "—"). Seed each position with its own identity so rotation
  // moves something visible and manual placement starts from the real arrangement.
  function composerPlugBoard(layout: Record<string, string | null>, cylinders: number): Record<string, string | null> {
    const positions = sparkPlugPositions(cylinders);
    if (positions.some((position) => layout[position])) return layout;
    const seeded: Record<string, string | null> = {};
    for (const position of positions) seeded[position] = position;
    return seeded;
  }

  // Manual placement grid. Each position is a dropdown over the plug-id universe
  // (1T…nB); a plug can only sit in one place, so a label chosen at one position
  // drops out of every other menu — the cascade the request asked for. Rendered from
  // whatever selections are live so re-rendering on change keeps the exclusions honest.
  function manualPlugBodyMarkup(currentLayout: Record<string, string | null>, cylinders: number): string {
    const labels = sparkPlugPositions(cylinders);
    const ids = Array.from(new Set([...labels, ...Object.values(currentLayout).filter((id): id is string => Boolean(id))]));
    const chosen = new Map<string, string>();
    for (const position of labels) {
      const value = currentLayout[position];
      if (value) chosen.set(position, value);
    }
    const cell = (position: string) => {
      const current = currentLayout[position] ?? null;
      const usedElsewhere = new Set<string>();
      for (const [pos, value] of chosen) if (pos !== position) usedElsewhere.add(value);
      const available = ids.filter((label) => label === current || !usedElsewhere.has(label));
      const options = `<option value=""${current ? "" : " selected"}>—</option>`
        + available.map((label) => `<option value="${escapeHtml(label)}"${label === current ? " selected" : ""}>${escapeHtml(label)}</option>`).join("");
      return `
        <label class="maintenance-plug-cell is-after">
          <small>${escapeHtml(position)}</small>
          <select data-mx-plug-after="${escapeHtml(position)}">${options}</select>
        </label>`;
    };
    return `
      <p class="maintenance-form-hint">Pick each position's plug (leave blank to clear). A plug sits in one place, so labels already chosen drop out of the other menus. This is also how labels get assigned the first time.</p>
      <div class="maintenance-plug-grid" style="--plug-cols: ${cylinders};">
        ${(["T", "B"] as const).map((row) => `
          <div class="maintenance-plug-row"><small>${row === "T" ? "Top" : "Bottom"}</small>
            ${Array.from({ length: cylinders }, (_, index) => cell(`${index + 1}${row}`)).join("")}
          </div>
        `).join("")}
      </div>
    `;
  }

  function plugComposerBodyMarkup(action: string): string {
    const { engineId, cylinders } = composerEngineContext();
    const editing = state.entries.find(entry => entry.id === state.editingEntryId)?.details?.sparkPlug;
    const { layout: currentLayout } = computeSparkPlugLayout(state.entries.filter(entry => entry.id !== state.editingEntryId), engineId, cylinders);
    const layout = editing?.beforeLayout ?? currentLayout;
    if (action === "replace" || action === "identify") {
      return `<p class="maintenance-form-hint">Select the positions being installed or identified. Give each physical plug a unique ID (up to 12 characters), and keep that ID when rotating or reinstalling it. Enter 0 prior hours for a new plug; leave blank if unknown. A previously tracked ID retains its recorded history. A known hour value can establish its total if the earlier total is unknown. Enter the configured meter reading for this placement.</p>
        <div class="maintenance-plug-install">${sparkPlugPositions(cylinders).map(position => `<div>
          <label><input type="checkbox" data-mx-plug-pos="${position}" ${!editing || editing.positions?.includes(position) ? "checked" : ""}> ${position}</label>
          <label class="upload-field"><span>Plug ID</span><input type="text" maxlength="12" aria-label="Plug ID at ${position}" data-mx-plug-id="${position}" value="${escapeHtml(editing?.afterLayout?.[position] ?? "")}" placeholder="e.g. P001"></label>
          <label class="upload-field"><span>Prior hours</span><input type="number" min="0" step="0.1" aria-label="Prior plug hours at ${position}" data-mx-plug-prior="${position}" value="${editing?.startingHours?.[editing?.afterLayout?.[position]] ?? ""}"></label>
        </div>`).join("")}</div>`;
    }
    if (action === "rotate" && editing?.afterLayout) return manualPlugBodyMarkup(editing.afterLayout, cylinders);
    if (action === "rotate") {
      const modes: Array<[string, string]> = [
        ["top-bottom", "Top ↔ bottom (same cylinder)"],
        ["advance", "Advance to next cylinder"],
        ["top-bottom-advance", "Top ↔ bottom + advance"],
      ];
      const board = composerPlugBoard(layout, cylinders);
      const after = rotateSparkPlugLayout(board, cylinders, state.composerPlugMode as any);
      return `
        <div class="maintenance-bs-tags">
          ${modes.map(([mode, label]) => `<button class="maintenance-bs-tag${state.composerPlugMode === mode ? " is-active" : ""}" type="button" data-mx-action="plug-helper" data-mx-mode="${mode}">${label}</button>`).join("")}
        </div>
        <p class="maintenance-form-hint">Before</p>
        ${plugGridMarkup(board, cylinders)}
        <p class="maintenance-form-hint">After</p>
        ${plugGridMarkup(after, cylinders, { after: true })}
      `;
    }
    if (action === "manual") {
      return manualPlugBodyMarkup(editing?.afterLayout ?? layout, cylinders);
    }
    return `
      <p class="maintenance-form-hint">Which positions were serviced?</p>
      <div class="maintenance-plug-grid" style="--plug-cols: ${cylinders};">
        ${(["T", "B"] as const).map((row) => `
          <div class="maintenance-plug-row"><small>${row === "T" ? "Top" : "Bottom"}</small>
            ${Array.from({ length: cylinders }, (_, index) => `
              <label class="maintenance-plug-cell maintenance-plug-check">
                <small>${index + 1}${row}</small>
                <input type="checkbox" checked data-mx-plug-pos="${index + 1}${row}" />
              </label>
            `).join("")}
          </div>
        `).join("")}
      </div>
    `;
  }

  function uiIcon(name: string): string {
    return `<img class="maintenance-ui-icon" src="/assets/vendor/feather/${name}.svg" alt="" aria-hidden="true" />`;
  }

  function selectedEngine() {
    const engines = draftBaseline().engines;
    return engines.find(engine => engine.engineId === state.selectedEngineId) ?? engines[0];
  }

  function selectedEngineLabel(): string {
    const engine = selectedEngine();
    return engineDisplayName(engine, draftBaseline().engines.indexOf(engine));
  }

  function meterContext(source = draftHourSource()) {
    const field = `${source}Hours` as "tachHours" | "hobbsHours" | "ttafHours";
    const rollups = computeMaintenanceRollups(state.baselineRow?.baseline, source, state.entries);
    const value = rollups?.currentHours[field] ?? null;
    const baselineHours = state.baselineRow?.baseline?.hours;
    const entry = state.entries.filter(row => row.status !== "deleted" && row[field] != null && Number(row[field]) === value)
      .sort((a, b) => `${b.occurredOn}|${b.createdAt ?? ""}`.localeCompare(`${a.occurredOn}|${a.createdAt ?? ""}`))[0];
    const date = entry?.occurredOn ?? (baselineHours?.[field] === value ? baselineHours?.recordedOn : null);
    return { field, value, date, label: MAINTENANCE_HOUR_SOURCE_LABELS[source] };
  }

  function workspaceHeaderMarkup(): string {
    const busy = state.composerOpen || state.editingEntryId || state.borescope.editingInspectionId || state.borescope.creating;
    return `<header class="maintenance-workspace-header">
      <div><h2>Maintenance</h2><p>${escapeHtml(aircraftDisplayLabel(state.aircraft ?? ""))}<span>${escapeHtml(aircraftMakeModelLabel(currentProfile()))}</span></p></div>
      <nav aria-label="Maintenance views">
        <button type="button" class="maintenance-view-action" data-mx-action="show-activity" ${busy ? "disabled" : ""} ${!state.systemsOpen && !state.editingBaseline ? 'aria-current="page"' : ""}>${uiIcon("clock")}Activity</button>
        <button type="button" class="maintenance-view-action" data-mx-action="show-systems" ${busy ? "disabled" : ""} ${state.systemsOpen ? 'aria-current="page"' : ""}>${uiIcon("settings")}Systems</button>
        <button type="button" class="maintenance-view-action" data-mx-action="edit-baseline" ${busy || appReadOnlyMode() ? "disabled" : ""}>${uiIcon("tool")}Setup</button>
      </nav>
    </header>`;
  }

  function contextStripMarkup(): string {
    const engines = draftBaseline().engines;
    if (engines.length < 2 || state.systemsOpen) return "";
    return `<section class="maintenance-engine-context" aria-label="Maintenance engine context">
      <span class="maintenance-context-label">Activity for</span>
      <div class="maintenance-engine-switch" role="group" aria-label="Maintenance engine">
        ${engines.map((engine, i) => `<button type="button" data-mx-action="select-engine" data-mx-engine-id="${escapeHtml(engine.engineId)}" aria-pressed="${engine.engineId === selectedEngine()?.engineId}" ${state.composerOpen || state.editingEntryId || state.borescope.editingInspectionId || state.borescope.creating ? "disabled" : ""}>${escapeHtml(engineDisplayName(engine, i))}</button>`).join("")}
      </div>
      <span class="maintenance-form-hint">Includes whole-aircraft records.</span>
    </section>`;
  }

  function compactOilComposerMarkup(editing: any): string {
    const meter = meterContext();
    const engine = editing ? draftBaseline().engines.find(row => row.engineId === editing.engineId) : selectedEngine();
    const engineId = engine?.engineId ?? "";
    const oil = editing?.details?.oil ?? {};
    return `<form class="maintenance-entry-composer maintenance-quick-oil" data-mx-entry-form data-mx-form-key="${escapeHtml(editing?.id ?? "quick-oil")}">
      <div class="maintenance-composer-heading"><h3>${uiIcon("droplet")}${editing ? "Edit oil entry" : "Add oil"}</h3><span>${escapeHtml(aircraftDisplayLabel(state.aircraft ?? ""))} / ${escapeHtml(engine ? engineDisplayName(engine, draftBaseline().engines.indexOf(engine)) : "Choose engine below")}</span><button type="button" class="maintenance-view-action" aria-label="Close oil entry" data-mx-action="cancel-entry">${uiIcon("x")}</button></div>
      <input type="hidden" data-mx-entry="entryKind" value="oil-added" />
      ${!editing ? `<input type="hidden" data-mx-entry="engineId" value="${escapeHtml(engineId)}" />` : ""}
      <div class="maintenance-quick-fields">
        <label class="upload-field"><span>Date <em class="maintenance-required">*</em></span><input type="date" required data-mx-entry="occurredOn" value="${escapeHtml(editing?.occurredOn ?? todayIsoDate())}" /></label>
        <label class="upload-field"><span>Quantity (qt) <em class="maintenance-required">*</em></span><input type="number" step="0.01" min="0.01" required inputmode="decimal" data-mx-oil-detail="quartsAdded" value="${oil.quartsAdded ?? ""}" /></label>
        <label class="upload-field"><span>${escapeHtml(meter.label)} reading (h)</span><input type="number" step="0.1" min="0" inputmode="decimal" data-mx-entry="${meter.field}" value="${editing?.[meter.field] ?? ""}" aria-describedby="maintenanceLastMeter" /><small id="maintenanceLastMeter">${meter.value == null ? "No previous reading recorded" : `Last recorded: ${formatHours(meter.value)} h${meter.date ? ` on ${escapeHtml(maintenanceDateLabel(meter.date))}` : "; date unavailable"}`}</small></label>
      </div>
      <details class="maintenance-optional" ${editing && !engineId ? "open" : ""}><summary>Optional details <span>(product, notes, attachments)</span></summary>
        <div class="aircraft-form-grid aircraft-form-grid-four">
          ${textFieldMarkup("Oil product", 'data-mx-oil-detail="product"', oil.product ?? null)}
          ${textFieldMarkup("Title", 'data-mx-entry="title"', editing?.title ?? "Oil added")}
          ${["tachHours", "hobbsHours", "ttafHours"].filter(field => field !== meter.field).map(field => hoursFieldMarkup(field === "ttafHours" ? "TTAF" : field === "tachHours" ? "Tach" : "Hobbs", `data-mx-entry="${field}"`, editing?.[field] ?? null)).join("")}
          ${editing ? `<label class="upload-field"><span>Engine</span><select data-mx-entry="engineId" required><option value="">Choose engine</option>${draftBaseline().engines.map((row, i) => `<option value="${escapeHtml(row.engineId)}" ${row.engineId === engineId ? "selected" : ""}>${escapeHtml(engineDisplayName(row, i))}</option>`).join("")}</select></label>` : ""}
        </div>
        <label class="upload-field"><span>Notes</span><textarea rows="2" maxlength="4000" data-mx-entry="notes">${escapeHtml(editing?.notes ?? "")}</textarea></label>
        <div class="maintenance-attach-row" data-mx-attach-list>${attachListMarkup(editing?.attachments ?? [])}</div>
      </details>
      ${statusMarkup()}
      <div class="maintenance-composer-actions"><button class="secondary-action" type="button" data-mx-action="cancel-entry">Cancel</button><button class="primary-action" type="submit">Save oil entry</button></div>
    </form>`;
  }

  function systemKinds(key: string): string[] {
    return ({ oil: ["oil", "oil-added"], cylinders: ["compression", "borescope"], ignition: ["mag-timing", "spark-plug"], propeller: ["propeller"] } as Record<string, string[]>)[key] ?? [];
  }

  function systemsMarkup(): string {
    const editing = borescopeInspectionById(state.borescope.editingInspectionId);
    if (editing?.status === "active") return borescopeEditorMarkup(editing);
    if (state.borescope.creating) return `${statusMarkup()}${borescopeModuleMarkup()}`;
    const groups = [
      { key: "oil", title: "Oil", description: "Changes, top-ups and recorded usage", body: oilModuleMarkup() },
      { key: "cylinders", title: "Cylinders", description: "Compression readings and borescope inspections", body: compressionModuleMarkup() + borescopeModuleMarkup() },
      { key: "ignition", title: "Ignition", description: "Magneto timing, inspections and spark plug service", body: magnetosModuleMarkup() + sparkPlugModuleMarkup() },
      { key: "propeller", title: "Propeller", description: "Overhaul times, installation and work records", body: propellerModuleMarkup() },
    ];
    return `${state.systemsOpen ? statusMarkup() : ""}<div class="maintenance-systems-heading"><h2>Systems</h2><p>Open a system for its records and service tools. Summaries include all configured engines.</p></div>
      ${groups.map(group => {
        const records = state.entries.filter(entry => entry.status !== "deleted" && systemKinds(group.key).includes(entry.entryKind));
        const dates = records.map(entry => entry.occurredOn).filter(Boolean);
        const inspections = group.key === "cylinders" ? state.borescope.inspections.filter(inspection => inspection.status !== "deleted") : [];
        dates.push(...inspections.map(inspection => inspection.inspectedOn).filter(Boolean));
        dates.sort((a, b) => String(b).localeCompare(String(a)));
        const count = records.length + inspections.length;
        const caption = count ? `${count} record${count === 1 ? "" : "s"}${dates[0] ? ` / Latest ${maintenanceDateLabel(dates[0])}` : ""}` : "No records yet";
        return `<details class="maintenance-system-group" data-mx-system="${group.key}" ${state.expandedSystems.includes(group.key) ? "open" : ""}>
          <summary><span><strong>${group.title}</strong><small>${group.description}</small></span><span class="maintenance-system-recency">${escapeHtml(caption)}</span>${uiIcon("chevron-right")}</summary>
          <div class="maintenance-system-history"><span>${group.key === "cylinders" ? "Cylinder history" : "Activity history"}</span>
            ${draftBaseline().engines.map((engine, index) => `<button class="secondary-action" type="button" data-mx-action="system-history" data-mx-system-key="${group.key}" data-mx-engine-id="${escapeHtml(engine.engineId)}">${escapeHtml(engineCount() > 1 ? `${engineDisplayName(engine, index)} records` : "View records")}</button>`).join("")}
          </div>
          <div class="maintenance-system-details">${group.body}</div>
        </details>`;
      }).join("")}`;
  }

  function activityRowsMarkup(): string {
    const query = state.historyQuery.trim().toLocaleLowerCase();
    const engineId = selectedEngine()?.engineId;
    const knownEngines = new Set(draftBaseline().engines.map(engine => engine.engineId));
    const meter = meterContext();
    const engineLabel = selectedEngineLabel();
    const inspections = state.borescope.inspections.map(inspection => ({
      id: `bs:${inspection.id}`, entryKind: "borescope", engineId: inspection.engineId, occurredOn: inspection.inspectedOn,
      createdAt: inspection.createdAt, title: `Borescope inspection ${inspection.sequence}`, notes: inspection.note,
      status: inspection.status, inspection, details: { images: (inspection.images ?? []).map((image: any) => ({ fileName: image.fileName, cylinder: image.cylinderNumber, note: image.note })) },
    }));
    const entries = [...state.entries, ...inspections].filter(entry => (!entry.engineId || entry.engineId === engineId || !knownEngines.has(entry.engineId))
      && (!state.historyKind || entry.entryKind === state.historyKind || state.historyKind.startsWith("system:") && systemKinds(state.historyKind.slice(7)).includes(entry.entryKind))
      && (!query || [entry.title, entry.notes, entry.occurredOn, MAINTENANCE_ENTRY_KIND_LABELS[entry.entryKind as keyof typeof MAINTENANCE_ENTRY_KIND_LABELS], JSON.stringify(entry.details ?? {}), ...(entry.attachments ?? []).map((attachment: any) => attachment.fileName)].join(" ").toLocaleLowerCase().includes(query)))
      .sort((a, b) => `${b.occurredOn}|${b.createdAt ?? ""}`.localeCompare(`${a.occurredOn}|${a.createdAt ?? ""}`));
    if (!entries.length) return `<p class="maintenance-timeline-empty">${query || state.historyKind ? "No records match these filters." : "No records for this engine yet. Record work or add an oil entry to get started."}</p>`;
    let previousMonth = "";
    return entries.map(entry => {
      const month = String(entry.occurredOn ?? "").slice(0, 7);
      const monthLabel = /^\d{4}-\d{2}$/.test(month) ? new Date(`${month}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "Undated";
      const heading = month !== previousMonth ? `<h3 class="maintenance-month-heading">${escapeHtml(monthLabel)}</h3>` : "";
      previousMonth = month;
      if (entry.inspection) {
        const inspection = entry.inspection;
        const scope = !inspection.engineId ? "Whole aircraft" : knownEngines.has(inspection.engineId) ? engineLabel : "Engine no longer configured";
        return `${heading}<details class="maintenance-activity-record" data-mx-record-id="${escapeHtml(entry.id)}"><summary>
          <time datetime="${escapeHtml(entry.occurredOn)}">${escapeHtml(maintenanceDateLabel(entry.occurredOn))}</time>
          <span class="maintenance-record-icon">${uiIcon("search")}</span>
          <span class="maintenance-record-title"><strong>${escapeHtml(entry.title)}</strong><small>${escapeHtml(scope)}${entry.status === "deleted" ? " / Deleted" : ""}</small></span>
          <span class="maintenance-record-meter">${inspection.images?.length ?? 0} images</span>${uiIcon("chevron-right")}
          </summary><div class="maintenance-bs-activity-detail">
          ${inspection.note ? `<p>${escapeHtml(inspection.note)}</p>` : ""}
          ${entry.status === "deleted" ? `<button class="secondary-action" type="button" data-mx-action="bs-restore-inspection" data-mx-inspection-id="${escapeHtml(inspection.id)}"${appReadOnlyMode() ? " disabled" : ""}>Restore inspection</button>` : `${borescopeCylinderSlots(inspection)}<button class="secondary-action" type="button" data-mx-action="bs-open-inspection" data-mx-inspection-id="${escapeHtml(inspection.id)}">View inspection</button>`}
          </div></details>`;
      }
      const oil = entry.entryKind === "oil-added";
      const scope = !entry.engineId ? "Whole aircraft" : knownEngines.has(entry.engineId) ? engineLabel : "Engine no longer configured";
      const detail = oil && entry.details?.oil?.quartsAdded != null ? `${formatOilQuantity(entry.details.oil.quartsAdded)} qt` : scope;
      return `${heading}<details class="maintenance-activity-record" data-mx-record-id="${escapeHtml(entry.id)}"><summary>
        <time datetime="${escapeHtml(entry.occurredOn)}">${escapeHtml(maintenanceDateLabel(entry.occurredOn))}</time>
        <span class="maintenance-record-icon ${oil ? "is-oil" : ""}">${uiIcon(oil || entry.entryKind === "oil" ? "droplet" : entry.entryKind === "hours" ? "clock" : "tool")}</span>
        <span class="maintenance-record-title"><strong>${escapeHtml(entry.title)}</strong><small>${escapeHtml(detail)}${entry.status === "deleted" ? " / Deleted" : ""}</small></span>
        <span class="maintenance-record-meter">${entry[meter.field] == null ? "No reading" : `${formatHours(entry[meter.field])} h`}</span>${uiIcon("chevron-right")}
      </summary>${timelineEntryMarkup(entry)}</details>`;
    }).join("");
  }

  function refreshActivityRows() {
    const list = contentEl()?.querySelector("[data-mx-activity-rows]");
    if (list) list.innerHTML = activityRowsMarkup();
    hydrateBorescopeThumbs();
  }

  function composerMarkup(): string {
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    const editing = state.editingEntryId
      ? state.entries.find((entry) => entry.id === state.editingEntryId) ?? null
      : null;
    if (!state.composerOpen && !editing) return "";
    const engines = baseline?.engines ?? [];
    const editingKind = editing ? normalizeMaintenanceEntryKind(editing.entryKind) : normalizeMaintenanceEntryKind(state.composerKind);
    if ((state.compactOil && !editing) || editing?.entryKind === "oil-added") return compactOilComposerMarkup(editing);
    const needsEngine = ["propeller", "oil", "oil-added"].includes(editingKind);
    const engineOptions = engines.length > 1
      ? `
        <label class="upload-field">
          <span>Engine</span>
          <select data-mx-entry="engineId"${needsEngine ? " required" : ""}>
            <option value="">${needsEngine ? "Choose engine" : "Whole aircraft"}</option>
            ${engines.map((engine, index) => `<option value="${escapeHtml(engine.engineId)}" ${editing?.engineId === engine.engineId ? "selected" : ""}>${escapeHtml(engineDisplayName(engine, index))}</option>`).join("")}
          </select>
        </label>
      `
      : "";
    const oilDetails = editing?.details?.oil ?? {};
    const compressionDetails = editing?.details?.compression ?? {};
    const compressionReadings: Array<number | null> = Array.isArray(compressionDetails.readings) ? compressionDetails.readings : [];
    // Readings grid sizes to the largest configured cylinder count; trailing
    // blanks drop at normalize time.
    const compressionCylinders = Math.max(1, ...(baseline?.engines ?? []).map((engine) => engine.cylinderCount ?? 0), compressionReadings.length, 4);
    const kindOptions = MAINTENANCE_ENTRY_KINDS.map((kind) => `
      <option value="${kind}" ${kind === editingKind ? "selected" : ""}>${MAINTENANCE_ENTRY_KIND_LABELS[kind]}</option>
    `).join("");
    const savedAttachments = Array.isArray(editing?.attachments) ? editing.attachments : [];
    const kindDefaultTitle = editingKind === "oil" || editingKind === "oil-added" ? MAINTENANCE_ENTRY_KIND_LABELS[editingKind]
      : editingKind === "hours" ? MAINTENANCE_ENTRY_KIND_LABELS.hours
      : editingKind === "compression" ? MAINTENANCE_ENTRY_KIND_LABELS.compression
        : editingKind === "mag-timing" ? MAINTENANCE_ENTRY_KIND_LABELS["mag-timing"]
          : editingKind === "spark-plug" ? MAINTENANCE_ENTRY_KIND_LABELS["spark-plug"]
            : editingKind === "propeller" ? MAINTENANCE_ENTRY_KIND_LABELS.propeller
              : "";
    const titleValue = editing?.title ?? kindDefaultTitle;
    const magTimingDetails = editing?.details?.magTiming ?? {};
    const plugDetails = editing?.details?.sparkPlug ?? null;
    const plugAction = plugDetails?.action ?? state.composerPlugAction;
    const propWorkType = editing?.details?.propeller?.workType ?? "inspection";
    const meter = meterContext();
    const extraMeters = [["tachHours", "Tach"], ["hobbsHours", "Hobbs"], ["ttafHours", "TTAF"]]
      .filter(([field]) => field !== meter.field);
    const hasOptionalDetails = Boolean(editing?.notes) || extraMeters.some(([field]) => editing?.[field] != null);
    return `
      <form class="maintenance-entry-composer" data-mx-entry-form data-mx-form-key="${escapeHtml(editing?.id ?? state.composerKind)}">
        <h3>${editing ? `Edit: ${escapeHtml(editing.title)}` : "Record work"}</h3>
        <div class="aircraft-form-grid aircraft-form-grid-four">
          <label class="upload-field">
            <span>Entry type</span>
            <select data-mx-entry="entryKind">${kindOptions}</select>
          </label>
          <label class="upload-field aircraft-form-span-2">
            <span>Title <em class="maintenance-required">*</em></span>
            <input type="text" maxlength="200" required placeholder="e.g. Oil and filter changed" data-mx-entry="title" value="${escapeHtml(titleValue)}" />
          </label>
          <label class="upload-field">
            <span>Date <em class="maintenance-required">*</em></span>
            <input type="date" required data-mx-entry="occurredOn" value="${escapeHtml(editing?.occurredOn ?? todayIsoDate())}" />
          </label>
          ${engineOptions}
          <label class="upload-field">
            <span>${escapeHtml(meter.label)} reading (h)</span>
            <input type="number" step="0.1" min="0" inputmode="decimal" data-mx-entry="${meter.field}" value="${editing?.[meter.field] ?? ""}" aria-describedby="maintenanceWorkLastMeter" />
            <small id="maintenanceWorkLastMeter">${meter.value == null ? "No previous reading recorded" : `Last recorded: ${formatHours(meter.value)} h${meter.date ? ` on ${escapeHtml(maintenanceDateLabel(meter.date))}` : "; date unavailable"}`}</small>
          </label>
        </div>
        <div class="maintenance-oil-fields" data-mx-compression-block ${editingKind === "compression" ? "" : "hidden"}>
          <span class="maintenance-oil-tag">Compression readings (over ${MAINTENANCE_COMPRESSION_OVER})</span>
          <div class="aircraft-form-grid aircraft-form-grid-four">
            <label class="upload-field">
              <span>Condition</span>
              <select data-mx-comp="condition">
                <option value="hot" ${compressionDetails.condition !== "cold" ? "selected" : ""}>Hot</option>
                <option value="cold" ${compressionDetails.condition === "cold" ? "selected" : ""}>Cold</option>
              </select>
            </label>
          </div>
          <div class="maintenance-comp-row">
            ${Array.from({ length: compressionCylinders }, (_, index) => `
              <label class="maintenance-comp-input">
                <small>Cyl ${index + 1}</small>
                <span class="maintenance-comp-pair">
                  <input type="number" step="1" min="0" max="99" inputmode="numeric" data-mx-comp-reading="${index}" value="${compressionReadings[index] ?? ""}" />/${MAINTENANCE_COMPRESSION_OVER}
                </span>
              </label>
            `).join("")}
          </div>
        </div>
        <div class="maintenance-oil-fields" data-mx-magtiming-block ${editingKind === "mag-timing" ? "" : "hidden"}>
          <span class="maintenance-oil-tag">Timing (degrees BTDC)</span>
          ${MAGNETO_POSITIONS.map((position) => {
            const check = (magTimingDetails.checks ?? []).find((item: any) => item.position === position) ?? {};
            return `
            <div class="maintenance-magt-row">
              <small>${position} mag</small>
              ${hoursFieldMarkup("Measured °", `data-mx-magt="${position}:measuredDeg"`, check.measuredDeg ?? null, { placeholder: "e.g. 24.5" })}
              ${hoursFieldMarkup("Set °", `data-mx-magt="${position}:setDeg"`, check.setDeg ?? null, { placeholder: "e.g. 25.0" })}
            </div>
          `;
          }).join("")}
        </div>
        <div class="maintenance-oil-fields" data-mx-plug-block ${editingKind === "spark-plug" ? "" : "hidden"}>
          <span class="maintenance-oil-tag">Spark plug service</span>
          <div class="aircraft-form-grid aircraft-form-grid-four">
            <label class="upload-field">
              <span>Action</span>
              <select data-mx-plug-action>
                ${SPARK_PLUG_ACTIONS.map((action) => `<option value="${action}" ${action === plugAction ? "selected" : ""}>${SPARK_PLUG_ACTION_LABELS[action]}</option>`).join("")}
              </select>
            </label>
          </div>
          <div data-mx-plug-body>${plugComposerBodyMarkup(plugAction)}</div>
        </div>
        <div class="maintenance-oil-fields" data-mx-propeller-block ${editingKind === "propeller" ? "" : "hidden"}>
          <span class="maintenance-oil-tag">Propeller work</span>
          <div class="aircraft-form-grid aircraft-form-grid-four">
            <label class="upload-field">
              <span>Work type</span>
              <select data-mx-prop-worktype>
                ${PROPELLER_WORK_TYPES.map((workType) => `<option value="${workType}" ${workType === propWorkType ? "selected" : ""}>${PROPELLER_WORK_TYPE_LABELS[workType]}</option>`).join("")}
              </select>
            </label>
          </div>
          <p class="maintenance-form-hint">An <b>Overhaul</b> starts a new since-overhaul clock. Enter the ${MAINTENANCE_HOUR_SOURCE_LABELS[draftHourSource()]} reading to calculate elapsed hours; otherwise the time stays unknown. For multiple engines, choose the engine for this propeller.</p>
        </div>
        <div class="maintenance-oil-fields" data-mx-oil-block ${editingKind === "oil" || editingKind === "oil-added" ? "" : "hidden"}>
          <span class="maintenance-oil-tag" data-mx-oil-heading>${editingKind === "oil-added" ? "Oil added between changes" : "Oil change details"}</span>
          <div class="aircraft-form-grid aircraft-form-grid-four">
            ${textFieldMarkup("Oil brand / type / viscosity", 'data-mx-oil-detail="product"', oilDetails.product ?? null, { placeholder: "e.g. Phillips X/C 20W-50" })}
            <label class="upload-field">
              <span data-mx-oil-quantity-label>${editingKind === "oil-added" ? "Quarts added" : "Refill quarts"}</span>
              <input type="number" step="0.01" min="${editingKind === "oil-added" ? "0.01" : "0"}" inputmode="decimal" data-mx-oil-detail="quartsAdded" value="${oilDetails.quartsAdded ?? ""}"${editingKind === "oil-added" ? " required" : ""} />
            </label>
            <label class="upload-field" data-mx-oil-change-only ${editingKind === "oil" ? "" : "hidden"}>
              <span>Filter changed</span>
              <select data-mx-oil-detail="filterChanged">
                <option value=""${typeof oilDetails.filterChanged !== "boolean" ? " selected" : ""}>Not recorded</option>
                <option value="yes"${oilDetails.filterChanged === true ? " selected" : ""}>Yes</option>
                <option value="no"${oilDetails.filterChanged === false ? " selected" : ""}>No</option>
              </select>
            </label>
            <div data-mx-oil-change-only ${editingKind === "oil" ? "" : "hidden"}>
              ${textFieldMarkup("Filter part / model", 'data-mx-oil-detail="filterPartNumber"', oilDetails.filterPartNumber ?? null)}
            </div>
            <label class="maintenance-checkfield" data-mx-oil-change-only ${editingKind === "oil" ? "" : "hidden"}>
              <input type="checkbox" data-mx-oil-detail="sampleSent" ${oilDetails.sampleSent === true ? "checked" : ""} />
              Sample sent for analysis
            </label>
          </div>
          <p class="maintenance-form-hint">Log top-ups as Oil added. Refill oil entered with an oil change is excluded from top-up rates. Attach oil analysis reports below.</p>
          <div class="maintenance-attach-row" data-mx-attach-list>
            ${attachListMarkup(savedAttachments)}
          </div>
        </div>
        <details class="maintenance-optional" ${hasOptionalDetails ? "open" : ""}>
          <summary>Optional details <span>(other meter readings, notes)</span></summary>
          <div class="aircraft-form-grid">
            ${extraMeters.map(([field, label]) => hoursFieldMarkup(label, `data-mx-entry="${field}"`, editing?.[field] ?? null)).join("")}
          </div>
          <label class="upload-field">
            <span>Notes</span>
            <textarea rows="3" maxlength="4000" placeholder="What was done, parts used, who performed the work" data-mx-entry="notes">${escapeHtml(editing?.notes ?? "")}</textarea>
          </label>
        </details>
        ${statusMarkup()}
        <div class="maintenance-composer-actions">
          <button class="secondary-action" type="button" data-mx-action="cancel-entry">Cancel</button>
          <button class="primary-action" type="submit">${editing ? "Save entry" : "Add entry"}</button>
        </div>
      </form>
    `;
  }

  function timelineEntryMarkup(entry: any): string {
    const readOnly = appReadOnlyMode();
    const deleted = entry.status === "deleted";
    const hours = [
      entry.tachHours !== null && entry.tachHours !== undefined ? `Tach <b>${formatHours(entry.tachHours)}</b>` : "",
      entry.hobbsHours !== null && entry.hobbsHours !== undefined ? `Hobbs <b>${formatHours(entry.hobbsHours)}</b>` : "",
      entry.ttafHours !== null && entry.ttafHours !== undefined ? `TTAF <b>${formatHours(entry.ttafHours)}</b>` : "",
    ].filter(Boolean).join("<span class=\"maintenance-hours-sep\"> · </span>");
    const baseline = state.baselineRow?.baseline as MaintenanceBaselineJson | undefined;
    const engineIndex = (baseline?.engines ?? []).findIndex((engine) => engine.engineId === entry.engineId);
    const engineName = entry.engineId && engineIndex >= 0
      ? engineDisplayName(baseline?.engines?.[engineIndex], engineIndex)
      : "";
    if (deleted) {
      return `
        <article class="maintenance-entry is-deleted" data-mx-entry-id="${escapeHtml(entry.id)}">
          <div class="maintenance-entry-main">
            <h4>${escapeHtml(entry.title)}</h4>
            <p>${escapeHtml(maintenanceDeleteWarningCopy())}</p>
          </div>
          <div class="maintenance-entry-meta">
            <strong>${escapeHtml(maintenanceDateLabel(entry.occurredOn))}</strong>
            <span class="maintenance-status is-amber">Deletes ${escapeHtml(purgeDateLabel(entry.deletedAt))}</span>
            <button class="secondary-action maintenance-entry-action" type="button" data-mx-action="restore-entry" data-mx-entry-id="${escapeHtml(entry.id)}"${readOnly ? " disabled" : ""}>Restore</button>
          </div>
        </article>
      `;
    }
    const entryKind = normalizeMaintenanceEntryKind(entry.entryKind);
    const isOil = entryKind === "oil" || entryKind === "oil-added";
    const isHours = entryKind === "hours";
    const isCompression = entryKind === "compression";
    const compression = isCompression ? entry.details?.compression ?? null : null;
    const compressionBits = compression?.readings?.length
      ? `${compression.readings.map((value: number | null) => value ?? "—").join(" / ")} <b>over ${compression.over ?? MAINTENANCE_COMPRESSION_OVER}</b><span class="maintenance-hours-sep"> · </span>${compression.condition === "cold" ? "Cold" : "Hot"}`
      : "";
    const magTiming = entryKind === "mag-timing" ? entry.details?.magTiming ?? null : null;
    const magTimingBits = magTiming?.checks?.length
      ? magTiming.checks.map((check: any) =>
        `${check.position === "left" ? "L" : "R"} measured <b>${check.measuredDeg ?? "—"}°</b> / set <b>${check.setDeg ?? "—"}°</b>`).join("<span class=\"maintenance-hours-sep\"> · </span>")
      : "";
    const sparkPlug = entryKind === "spark-plug" ? entry.details?.sparkPlug ?? null : null;
    const sparkPlugBits = sparkPlug
      ? `<b>${escapeHtml(SPARK_PLUG_ACTION_LABELS[sparkPlug.action as keyof typeof SPARK_PLUG_ACTION_LABELS] ?? sparkPlug.action)}</b>${sparkPlug.action === "rotate" || sparkPlug.action === "manual"
        ? " — placement updated"
        : sparkPlug.positions?.length ? ` — ${escapeHtml(sparkPlug.positions.join(", "))}` : ""}`
      : "";
    const propeller = entryKind === "propeller" ? entry.details?.propeller ?? null : null;
    const propellerBits = propeller
      ? `<b>${escapeHtml(PROPELLER_WORK_TYPE_LABELS[propeller.workType as keyof typeof PROPELLER_WORK_TYPE_LABELS] ?? propeller.workType)}</b>`
      : "";
    const oil = isOil ? entry.details?.oil ?? {} : {};
    const oilBits = isOil ? [
      oil.product ? `Oil <b>${escapeHtml(oil.product)}</b>` : "",
      oil.quartsAdded !== null && oil.quartsAdded !== undefined ? `Quarts <b>${formatOilQuantity(oil.quartsAdded)}</b>` : "",
      typeof oil.filterChanged === "boolean" ? `Filter changed <b>${oil.filterChanged ? "Yes" : "No"}</b>` : "",
      oil.filterPartNumber ? `Filter <b>${escapeHtml(oil.filterPartNumber)}</b>` : "",
      oil.sampleSent ? "Sample <b>sent</b>" : "",
    ].filter(Boolean).join("<span class=\"maintenance-hours-sep\"> · </span>") : "";
    const attachments = Array.isArray(entry.attachments) ? entry.attachments : [];
    const attachmentLinks = attachments.map((attachment: any) => `
      <button class="maintenance-attach-link" type="button" data-mx-action="open-attachment" data-mx-attachment-id="${escapeHtml(attachment.id)}">
        ${attachmentIcon(attachment.contentType)} ${escapeHtml(attachment.fileName)}
      </button>
    `).join("");
    return `
      <article class="maintenance-entry" data-mx-entry-id="${escapeHtml(entry.id)}">
        <div class="maintenance-entry-main">
          <h4>${escapeHtml(entry.title)}${isOil ? ` <span class="maintenance-engine-chip">${MAINTENANCE_ENTRY_KIND_LABELS[entryKind]}</span>` : ""}${isHours ? ' <span class="maintenance-engine-chip">Hours</span>' : ""}${isCompression ? ' <span class="maintenance-engine-chip">Compression</span>' : ""}${magTiming ? ' <span class="maintenance-engine-chip">Mag timing</span>' : ""}${sparkPlug ? ' <span class="maintenance-engine-chip">Spark plugs</span>' : ""}${propeller ? ' <span class="maintenance-engine-chip">Propeller</span>' : ""}${engineName ? ` <span class="maintenance-engine-chip">${escapeHtml(engineName)}</span>` : ""}</h4>
          ${entry.notes ? `<p>${escapeHtml(entry.notes)}</p>` : ""}
          ${oilBits ? `<div class="maintenance-entry-hours">${oilBits}</div>` : ""}
          ${compressionBits ? `<div class="maintenance-entry-hours">${compressionBits}</div>` : ""}
          ${magTimingBits ? `<div class="maintenance-entry-hours">${magTimingBits}</div>` : ""}
          ${sparkPlugBits ? `<div class="maintenance-entry-hours">${sparkPlugBits}</div>` : ""}
          ${propellerBits ? `<div class="maintenance-entry-hours">${propellerBits}</div>` : ""}
          ${hours ? `<div class="maintenance-entry-hours">${hours}</div>` : ""}
          ${attachmentLinks ? `<div class="maintenance-attach-row">${attachmentLinks}</div>` : ""}
        </div>
        <div class="maintenance-entry-meta">
          <strong>${escapeHtml(maintenanceDateLabel(entry.occurredOn))}</strong>
          <div class="maintenance-entry-actions">
            <button class="secondary-action maintenance-entry-action" type="button" data-mx-action="edit-entry" data-mx-entry-id="${escapeHtml(entry.id)}"${readOnly ? " disabled" : ""}>Edit</button>
            <button class="secondary-action danger-action maintenance-entry-action" type="button" data-mx-action="delete-entry" data-mx-entry-id="${escapeHtml(entry.id)}"${readOnly ? " disabled" : ""}>Delete</button>
          </div>
        </div>
      </article>
    `;
  }

  function timelineMarkup({ locked = false }: { locked?: boolean } = {}): string {
    const readOnly = appReadOnlyMode();
    if (locked) {
      const gaps = baselineGaps();
      return `
        <section class="panel maintenance-live-panel is-locked">
          <div class="panel-header compact-header">
            <div>
              <p class="eyebrow">Timeline</p>
              <h2>Maintenance timeline</h2>
            </div>
            <button class="primary-action" type="button" disabled>+ Add entry</button>
          </div>
          <p class="maintenance-timeline-locked">Complete the required baseline fields to start adding entries.${gaps.length && state.baselineRow ? ` Missing: ${escapeHtml(gaps.join("; "))}.` : ""}</p>
        </section>
      `;
    }
    const busy = state.composerOpen || state.editingEntryId;
    return `<section class="maintenance-activity" aria-label="Maintenance activity">
      <div class="maintenance-activity-heading"><h2>Activity <small>${escapeHtml(selectedEngineLabel())} records</small></h2>
        <div class="maintenance-activity-actions">
          <button class="secondary-action" type="button" data-mx-action="open-hours-update" ${readOnly || busy ? "disabled" : ""}>${uiIcon("clock")}Update hours</button>
          <button class="secondary-action" type="button" data-mx-action="open-composer" ${readOnly || busy ? "disabled" : ""}>${uiIcon("tool")}Record work</button>
          <button class="primary-action" type="button" data-mx-action="open-oil-added" ${readOnly || busy ? "disabled" : ""}>${uiIcon("droplet")}Add oil</button>
        </div>
      </div>
      ${composerMarkup()}
      ${!busy && !state.systemsOpen ? statusMarkup() : ""}
      <div class="maintenance-history-filters">
        <label class="maintenance-history-search">${uiIcon("search")}<input type="search" aria-label="Search records" placeholder="Search records..." data-mx-history-search value="${escapeHtml(state.historyQuery)}" /></label>
        <select aria-label="Filter records by type" data-mx-history-kind><option value="">Type: All</option>${["oil", "cylinders", "ignition", "propeller"].map(key => `<option value="system:${key}" ${state.historyKind === `system:${key}` ? "selected" : ""}>${key === "cylinders" ? "Cylinder" : key[0].toUpperCase() + key.slice(1)} records</option>`).join("")}<option value="borescope" ${state.historyKind === "borescope" ? "selected" : ""}>Borescope inspections</option>${MAINTENANCE_ENTRY_KINDS.map(kind => `<option value="${kind}" ${state.historyKind === kind ? "selected" : ""}>${MAINTENANCE_ENTRY_KIND_LABELS[kind]}</option>`).join("")}</select>
      </div>
      <div class="maintenance-timeline" data-mx-activity-rows>${activityRowsMarkup()}</div>
    </section>`;
  }

  // ---------------------------------------------------------------- render --

  function renderContent() {
    const content = contentEl();
    if (!content) return;
    if (state.loading && !state.loaded) {
      content.innerHTML = noticeMarkup("Loading maintenance records…");
      return;
    }
    if (state.error) {
      content.innerHTML = `
        ${noticeMarkup(state.error, { tone: "error" })}
        <button class="secondary-action maintenance-retry" type="button" data-mx-action="retry">Try again</button>
      `;
      return;
    }
    const previousForm = content.querySelector<HTMLElement>("[data-mx-entry-form]");
    const formKey = previousForm?.dataset.mxFormKey;
    const values = Array.from(previousForm?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input:not([type=file]), textarea, select") ?? []).map(input => ({ value: input.value, checked: (input as HTMLInputElement).checked }));
    const optionalOpen = previousForm?.querySelector<HTMLDetailsElement>(".maintenance-optional")?.open;
    const expanded = Array.from(content.querySelectorAll<HTMLElement>("[data-mx-record-id][open]")).map(row => row.dataset.mxRecordId);
    const complete = baselineGaps().length === 0;
    if (!complete || state.editingBaseline) {
      const existingForm = content.querySelector<HTMLElement>("[data-mx-baseline-form]");
      if (existingForm) { refreshSetupReadiness(existingForm); paintStatus(); return; }
      const intro = state.baselineRow
        ? ""
        : noticeMarkup("Set up the powerplant baseline to start keeping maintenance records for this aircraft. Computed times (TSMOH, prop time) build from this baseline.");
      content.innerHTML = `${workspaceHeaderMarkup()}${intro}${setupFormMarkup()}${!complete ? timelineMarkup({ locked: true }) : ""}`;
      refreshSetupReadiness(content.querySelector<HTMLElement>("[data-mx-baseline-form]")!);
      return;
    }
    content.innerHTML = `${workspaceHeaderMarkup()}${kpiCardsMarkup()}${contextStripMarkup()}
      <div data-mx-activity-view ${state.systemsOpen ? "hidden" : ""}>${timelineMarkup()}</div>
      <div class="maintenance-systems-view" data-mx-systems-view ${state.systemsOpen ? "" : "hidden"}>${systemsMarkup()}</div>`;
    const form = content.querySelector<HTMLElement>("[data-mx-entry-form]");
    if (form && formKey && form.dataset.mxFormKey === formKey) {
      form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input:not([type=file]), textarea, select").forEach((input, i) => {
        if (!values[i]) return;
        input.value = values[i].value;
        if (input instanceof HTMLInputElement) input.checked = values[i].checked;
      });
      const optional = form.querySelector<HTMLDetailsElement>(".maintenance-optional");
      if (optional && optionalOpen) optional.open = true;
    }
    content.querySelectorAll<HTMLDetailsElement>("[data-mx-record-id]").forEach(row => { row.open = expanded.includes(row.dataset.mxRecordId); });
    hydrateBorescopeThumbs();
    bindFileInputs();
  }

  function renderAircraftMaintenancePanel(aircraft: string | null | undefined) {
    const panel = panelEl();
    const content = contentEl();
    if (!panel || !content) return;
    const enabled = maintenanceFeatureEnabled();
    panel.classList.toggle("is-maintenance-live", enabled);
    content.hidden = !enabled;
    if (!enabled) {
      // Flag off: leave the existing preview + Coming Soon treatment exactly
      // as production shows it today.
      content.innerHTML = "";
      state = freshState();
      return;
    }
    wireEvents();
    if (!aircraft) {
      state = freshState();
      content.innerHTML = noticeMarkup("Select an aircraft to view its maintenance records.");
      return;
    }
    const profile = getAircraftProfiles()?.[aircraft] ?? null;
    const aircraftId = profile?.databaseId ?? null;
    if (!aircraftId) {
      state = freshState();
      state.aircraft = aircraft;
      content.innerHTML = noticeMarkup("Maintenance records need this aircraft saved to the database. Save the aircraft profile first, then return here.");
      return;
    }
    if (appReadOnlyMode()) { state.composerOpen = false; state.editingEntryId = null; state.editingBaseline = false; }
    if (state.aircraft !== aircraft || state.aircraftId !== aircraftId) {
      releaseBorescopeThumbs();
      const preferences = { systemsOpen: state.systemsOpen, editingBaseline: state.editingBaseline, expandedSystems: [...state.expandedSystems], historyKind: state.historyKind };
      state = { ...freshState(), ...preferences };
      content.innerHTML = "";
      state.aircraft = aircraft;
      state.aircraftId = aircraftId;
    }
    if (!state.loaded && !state.loading) {
      loadMaintenance();
    }
    if (!state.borescope.loaded && !state.borescope.loading) {
      loadBorescope();
    }
    renderContent();
  }

  // ---------------------------------------------------------------- events --

  let refreshingSetupReadiness = false;
  function refreshSetupReadiness(form: HTMLElement) {
    if (refreshingSetupReadiness) return;
    refreshingSetupReadiness = true;
    try {
      form.querySelectorAll<HTMLInputElement>('[data-mx-eng$=":engineTotalTimeHours"], [data-mx-eng$=":tsmohHours"]').forEach(input => { input.required = true; });
      const { baseline, hourSource } = readBaselineFromForm(form);
      const gaps = maintenanceBaselineRequiredGaps(baseline, hourSource);
      const notice = form.querySelector<HTMLElement>("[data-mx-setup-readiness]");
      const savedStatus = !state.baselineRow ? "Not saved yet." : baselineGaps().length ? "A draft is saved; logging is locked." : "Saved baseline is ready for logging.";
      if (notice) notice.innerHTML = gaps.length
        ? `<strong>Required values missing</strong><p>${savedStatus} You can save these edits as a draft. Complete these values to unlock logging:</p><ul>${gaps.map(gap => `<li>${escapeHtml(gap)}</li>`).join("")}</ul>`
        : `<strong>Required values complete</strong><p>${savedStatus} Save to apply these values. Optional equipment history can be added later.</p>`;
      const actions = contentEl()?.querySelector("[data-mx-setup-panel] .maintenance-header-actions");
      if (state.baselineRow && actions && !actions.querySelector('[data-mx-action="cancel-baseline"]')) actions.insertAdjacentHTML("afterbegin", '<button class="secondary-action" type="button" data-mx-action="cancel-baseline">Cancel</button>');
      const oilLabel = form.querySelector('[data-mx-oil="lastChangeHours"]')?.closest("label")?.querySelector("span");
      if (oilLabel) oilLabel.textContent = `Last oil change ${MAINTENANCE_HOUR_SOURCE_LABELS[hourSource]}`;
      const button = contentEl()?.querySelector<HTMLButtonElement>('[data-mx-action="save-baseline"]');
      if (button && !state.savingBaseline) button.textContent = gaps.length ? "Save draft" : "Save baseline";
      const required = form.querySelector("[data-mx-required-meters]");
      const optional = form.querySelector("[data-mx-other-meters]");
      for (const source of MAINTENANCE_HOUR_SOURCES) {
        const input = form.querySelector<HTMLInputElement>(`[data-mx-hours="${source}Hours"]`);
        const label = input?.closest("label");
        if (!input || !label) continue;
        input.required = source === "ttaf" || source === hourSource;
        label.querySelector(".maintenance-required")?.remove();
        if (input.required) label.querySelector("span")?.insertAdjacentHTML("beforeend", ' <em class="maintenance-required">*</em>');
        const parent = input.required ? required : optional;
        if (parent && label.parentElement !== parent) parent.append(label);
      }
    } finally { refreshingSetupReadiness = false; }
  }

  function readBaselineFromForm(form: HTMLElement): { baseline: MaintenanceBaselineJson; hourSource: MaintenanceHourSource } {
    const draft = draftBaseline();
    const engines = draft.engines.map((engine, index) => {
      const next: any = { ...engine };
      form.querySelectorAll(`[data-mx-eng^="${index}:"]`).forEach((input) => {
        const field = (input as HTMLInputElement).dataset.mxEng?.split(":")[1];
        if (field) next[field] = (input as HTMLInputElement).value;
      });
      return next;
    });
    const propellers = draft.propellers.map((prop, index) => {
      const next: any = { ...prop };
      form.querySelectorAll(`[data-mx-prop^="${index}:"]`).forEach((input) => {
        const field = (input as HTMLInputElement).dataset.mxProp?.split(":")[1];
        if (field) next[field] = (input as HTMLInputElement).value;
      });
      return next;
    });
    const hours: any = { ...draft.hours, recordedOn: draft.hours.recordedOn ?? todayIsoDate() };
    form.querySelectorAll("[data-mx-hours]").forEach((input) => {
      const field = (input as HTMLInputElement).dataset.mxHours;
      if (field) hours[field] = (input as HTMLInputElement).value;
    });
    const sourceSelect = form.querySelector("[data-mx-source]") as HTMLSelectElement | null;
    const oil: any = { ...draft.oil };
    form.querySelectorAll("[data-mx-oil]").forEach((input) => {
      const field = (input as HTMLInputElement).dataset.mxOil;
      if (field) oil[field] = (input as HTMLInputElement).value;
    });
    const ignition: any = { magInspectionIntervalHours: draft.ignition?.magInspectionIntervalHours ?? null, magnetos: [] };
    form.querySelectorAll("[data-mx-ign]").forEach((input) => {
      const field = (input as HTMLInputElement).dataset.mxIgn;
      if (field) ignition[field] = (input as HTMLInputElement).value;
    });
    const magDrafts = new Map<string, any>();
    form.querySelectorAll("[data-mx-mag]").forEach((input) => {
      const [engineIndex, position, field] = ((input as HTMLInputElement).dataset.mxMag ?? "").split(":");
      const engineId = engines[Number(engineIndex)]?.engineId;
      if (!engineId || !position || !field) return;
      const key = `${engineId}:${position}`;
      const mag = magDrafts.get(key) ?? { engineId, position };
      mag[field] = (input as HTMLInputElement).value;
      magDrafts.set(key, mag);
    });
    ignition.magnetos = Array.from(magDrafts.values());
    return {
      baseline: normalizeMaintenanceBaselineJson({ engines, propellers, hours, oil, ignition }),
      hourSource: normalizeMaintenanceHourSource(sourceSelect?.value),
    };
  }

  function readEntryFromForm(form: HTMLElement): Record<string, unknown> {
    const entry: Record<string, unknown> = {};
    form.querySelectorAll("[data-mx-entry]").forEach((input) => {
      const field = (input as HTMLInputElement).dataset.mxEntry;
      if (field) entry[field] = (input as HTMLInputElement | HTMLTextAreaElement).value;
    });
    const kind = normalizeMaintenanceEntryKind(entry.entryKind);
    if (kind === "oil" || kind === "oil-added") {
      const oil: Record<string, unknown> = {};
      form.querySelectorAll("[data-mx-oil-detail]").forEach((input) => {
        const field = (input as HTMLInputElement).dataset.mxOilDetail;
        if (!field) return;
        oil[field] = (input as HTMLInputElement).type === "checkbox"
          ? (input as HTMLInputElement).checked
          : (input as HTMLInputElement).value;
      });
      oil.filterChanged = oil.filterChanged === "yes" ? true : oil.filterChanged === "no" ? false : null;
      entry.details = { oil };
    } else if (kind === "compression") {
      const readings: Array<string> = [];
      form.querySelectorAll("[data-mx-comp-reading]").forEach((input) => {
        const index = Number((input as HTMLInputElement).dataset.mxCompReading);
        if (Number.isInteger(index)) readings[index] = (input as HTMLInputElement).value;
      });
      const condition = (form.querySelector('[data-mx-comp="condition"]') as HTMLSelectElement | null)?.value ?? "hot";
      entry.details = { compression: { condition, readings } };
    } else if (kind === "mag-timing") {
      const checks: Record<string, any> = {};
      form.querySelectorAll("[data-mx-magt]").forEach((input) => {
        const [position, field] = ((input as HTMLInputElement).dataset.mxMagt ?? "").split(":");
        if (!position || !field) return;
        checks[position] = { ...(checks[position] ?? { position }), [field]: (input as HTMLInputElement).value };
      });
      entry.details = { magTiming: { checks: Object.values(checks) } };
    } else if (kind === "spark-plug") {
      const action = (form.querySelector("[data-mx-plug-action]") as HTMLSelectElement | null)?.value ?? "clean";
      const { engineId, cylinders } = composerEngineContext();
      const editing = state.entries.find(entry => entry.id === state.editingEntryId)?.details?.sparkPlug;
      const { layout: currentLayout } = computeSparkPlugLayout(state.entries.filter(entry => entry.id !== state.editingEntryId), engineId, cylinders);
      const layout = editing?.beforeLayout ?? currentLayout;
      const sparkPlug: Record<string, unknown> = { action };
      if (action === "rotate" && !form.querySelector("[data-mx-plug-after]")) {
        const board = composerPlugBoard(layout, cylinders);
        sparkPlug.beforeLayout = board;
        sparkPlug.afterLayout = rotateSparkPlugLayout(board, cylinders, state.composerPlugMode as any);
        sparkPlug.positions = sparkPlugPositions(cylinders);
      } else if (action === "manual" || action === "rotate") {
        const after: Record<string, string | null> = {};
        form.querySelectorAll("[data-mx-plug-after]").forEach((input) => {
          const position = (input as HTMLInputElement).dataset.mxPlugAfter;
          if (position) after[position] = (input as HTMLInputElement).value.trim() || null;
        });
        sparkPlug.beforeLayout = layout;
        sparkPlug.afterLayout = after;
        sparkPlug.positions = Object.keys(after);
      } else {
        sparkPlug.positions = Array.from(form.querySelectorAll("[data-mx-plug-pos]:checked"))
          .map((input) => (input as HTMLInputElement).dataset.mxPlugPos ?? "")
          .filter(Boolean);
      }
      if (action === "replace" || action === "identify") {
        const after = { ...layout };
        const startingHours: Record<string, string | null> = {};
        for (const position of sparkPlug.positions as string[]) {
          const id = form.querySelector<HTMLInputElement>(`[data-mx-plug-id="${position}"]`)?.value.trim() || null;
          after[position] = id;
          if (id) startingHours[id] = form.querySelector<HTMLInputElement>(`[data-mx-plug-prior="${position}"]`)?.value || null;
        }
        const legacyEdit = editing?.action === "replace" && !editing.startingHours && !Object.keys(startingHours).length;
        if (!legacyEdit) {
          sparkPlug.beforeLayout = layout;
          sparkPlug.afterLayout = after;
          sparkPlug.startingHours = startingHours;
        }
      }
      entry.details = { sparkPlug };
    } else if (kind === "propeller") {
      const workType = (form.querySelector("[data-mx-prop-worktype]") as HTMLSelectElement | null)?.value ?? "inspection";
      entry.details = { propeller: { workType } };
    }
    return entry;
  }

  async function uploadPendingAttachments(entryId: string): Promise<string | null> {
    const pending = state.pendingAttachments.splice(0);
    for (const file of pending) {
      try {
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        const prepared = isPdf
          ? { body: file as Blob, fileName: file.name, contentType: "application/pdf" }
          : await prepareImageForUpload(file, MAINTENANCE_ATTACHMENT_MAX_BYTES);
        if ("error" in prepared) return prepared.error;
        const buffer = await prepared.body.arrayBuffer();
        await maintenanceFetch(`/entries/${encodeURIComponent(entryId)}/attachments?filename=${encodeURIComponent(prepared.fileName)}`, {
          method: "POST",
          headers: { "content-type": prepared.contentType },
          body: buffer,
        });
      } catch (error: any) {
        // Put the unsent remainder back so a retry re-attempts them.
        state.pendingAttachments.unshift(...pending.slice(pending.indexOf(file) + 1));
        return `“${file.name}” did not upload: ${error?.message ?? "unknown error"}`;
      }
    }
    return null;
  }

  async function saveBaseline(form: HTMLElement) {
    if (appReadOnlyMode() || state.savingBaseline) return;
    // Drafts may omit required values, but entered values must still be valid.
    const invalid = Array.from(form.querySelectorAll<HTMLInputElement>("input")).find(input => input.validity.badInput || input.value !== "" && !input.validity.valid);
    if (invalid) {
      const details = invalid.closest<HTMLDetailsElement>("details");
      if (details) details.open = true;
      invalid.reportValidity();
      return;
    }
    const { baseline, hourSource } = readBaselineFromForm(form);
    state.savingBaseline = true;
    const button = contentEl()?.querySelector<HTMLButtonElement>('[data-mx-action="save-baseline"]');
    if (button) button.disabled = true;
    setStatus("Saving baseline...");
    paintStatus();
    try {
      const payload = await maintenanceFetch("/baseline", { method: "PUT", body: JSON.stringify({ baseline, hourSource }) });
      state.baselineRow = payload?.baseline ?? state.baselineRow;
      state.formDirty = false;
      state.editingBaseline = false;
      const incomplete = baselineGaps().length > 0;
      setStatus(incomplete ? "Draft saved. Complete the required values to unlock logging." : "Baseline saved. You can log maintenance records.");
      renderContent();
      if (!incomplete) contentEl()?.querySelector<HTMLElement>(state.systemsOpen ? '[data-mx-action="show-systems"]' : '[data-mx-action="open-composer"]')?.focus();
    } catch (error: any) {
      setStatus(error?.message ?? "Could not save the baseline.", { isError: true });
      paintStatus();
    } finally {
      state.savingBaseline = false;
      if (button) button.disabled = appReadOnlyMode();
    }
  }

  async function submitEntry(form: HTMLElement) {
    if (appReadOnlyMode() || state.savingEntry) return;
    const entry = readEntryFromForm(form);
    if ((entry.details as { sparkPlug?: { startingHours?: unknown } })?.sparkPlug?.startingHours) {
      const source = state.baselineRow?.hourSource ?? "tach";
      if (entry[`${source}Hours`] === null || entry[`${source}Hours`] === "" || entry[`${source}Hours`] === undefined) {
        setStatus(`Enter the ${MAINTENANCE_HOUR_SOURCE_LABELS[source]} reading to start tracking plug hours.`, { isError: true });
        paintStatus();
        form.querySelector<HTMLInputElement>(`[data-mx-entry="${source}Hours"]`)?.focus();
        return;
      }
    }
    state.savingEntry = true;
    const controls = Array.from(form.querySelectorAll<HTMLButtonElement>("button"));
    controls.forEach(button => { button.disabled = true; });
    setStatus("Saving entry...");
    paintStatus();
    let entrySaved = false;
    try {
      let savedEntryId = state.editingEntryId;
      if (state.editingEntryId) {
        await maintenanceFetch(`/entries/${encodeURIComponent(state.editingEntryId)}`, {
          method: "PUT",
          body: JSON.stringify(entry),
        });
      } else {
        const payload = await maintenanceFetch("/entries", { method: "POST", body: JSON.stringify(entry) });
        savedEntryId = payload?.entry?.id ?? null;
      }
      entrySaved = true;
      state.formDirty = false;
      let attachmentError: string | null = null;
      if (savedEntryId && state.pendingAttachments.length) {
        setStatus("Uploading attachments...");
        paintStatus();
        attachmentError = await uploadPendingAttachments(savedEntryId);
      }
      state.composerOpen = false;
      state.editingEntryId = null;
      state.pendingAttachments = [];
      setStatus(attachmentError ?? (entry.entryKind === "oil-added" ? "Oil entry saved." : "Entry saved."), { isError: Boolean(attachmentError) });
      state.historyQuery = "";
      state.historyKind = "";
      if (entry.engineId) state.selectedEngineId = String(entry.engineId);
      await refreshEntries();
      renderContent();
      contentEl()?.querySelector<HTMLElement>(`[data-mx-action="${state.returnAction}"]`)?.focus({ preventScroll: true });
    } catch (error: any) {
      setStatus(entrySaved ? "Entry saved, but records could not reload. Refresh the page to see it." : error?.message ?? "Could not save the entry.", { isError: true });
      if (entrySaved) renderContent();
      else paintStatus();
    } finally {
      state.savingEntry = false;
      controls.forEach(button => { button.disabled = false; });
    }
  }

  async function openAttachment(attachmentId: string) {
    try {
      // JWT-gated endpoint: top-level navigation can't carry the bearer token,
      // so fetch the bytes and open a blob URL (same flow as flight reports).
      const response = await fetch(`/api/aircraft/${encodeURIComponent(state.aircraftId ?? "")}/maintenance/attachments/${encodeURIComponent(attachmentId)}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`Attachment HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      setStatus(error?.message ?? "Could not open the attachment.", { isError: true });
      renderContent();
    }
  }

  async function deleteAttachment(attachmentId: string) {
    if (appReadOnlyMode()) return;
    if (!window.confirm("Delete this attachment? This cannot be undone.")) return;
    try {
      await maintenanceFetch(`/attachments/${encodeURIComponent(attachmentId)}`, { method: "DELETE" });
      await refreshEntries();
      setStatus(null);
    } catch (error: any) {
      setStatus(error?.message ?? "Could not delete the attachment.", { isError: true });
    }
    renderContent();
  }

  async function refreshEntries() {
    const loadState = state;
    const payload = await maintenanceFetch("");
    if (state !== loadState) return;
    state.baselineRow = payload?.baseline ?? state.baselineRow;
    state.entries = Array.isArray(payload?.entries) ? payload.entries : state.entries;
  }

  async function deleteEntry(entryId: string) {
    if (appReadOnlyMode()) return;
    const entry = state.entries.find((item) => item.id === entryId);
    const confirmed = window.confirm(`Delete “${entry?.title ?? "this entry"}”?\n\n${maintenanceDeleteWarningCopy()}`);
    if (!confirmed) return;
    try {
      await maintenanceFetch(`/entries/${encodeURIComponent(entryId)}`, { method: "DELETE" });
      await refreshEntries();
      setStatus(null);
    } catch (error: any) {
      setStatus(error?.message ?? "Could not delete the entry.", { isError: true });
    }
    renderContent();
  }

  async function restoreEntry(entryId: string) {
    if (appReadOnlyMode()) return;
    try {
      await maintenanceFetch(`/entries/${encodeURIComponent(entryId)}/restore`, { method: "POST" });
      await refreshEntries();
      setStatus(null);
    } catch (error: any) {
      setStatus(error?.message ?? "Could not restore the entry.", { isError: true });
    }
    renderContent();
  }

  // File inputs get DIRECT listeners, not delegation: while the OS file
  // dialog is open, a background re-render (flight-flag hydration, upload
  // polling → renderAircraftWorkspace → renderContent) can replace the
  // panel's DOM. The change event then fires on a detached input, which
  // never bubbles to the container — the classic "picked a file and nothing
  // happened". A listener bound on the input itself still fires. Binding is
  // idempotent (same function reference), so re-binding after each render
  // is safe.
  function onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.matches("[data-mx-attach-input]")) {
      const files = Array.from(input.files ?? []);
      input.value = "";
      if (!files.length) return;
      // PDFs can't be downscaled, so their cap is the stored cap; images may
      // be picked up to 20 MB and get downscaled at upload time.
      const isPdf = (file: File) => file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      const pdfSplit = splitOversizedFiles(files.filter(isPdf), MAINTENANCE_ATTACHMENT_MAX_BYTES);
      const imageSplit = splitOversizedFiles(files.filter((file) => !isPdf(file)), MAINTENANCE_IMAGE_SELECT_MAX_BYTES);
      state.pendingAttachments.push(...pdfSplit.accepted, ...imageSplit.accepted);
      const rejectedMessage = [pdfSplit.rejectedMessage, imageSplit.rejectedMessage].filter(Boolean).join(" ") || null;
      setStatus(rejectedMessage, { isError: Boolean(rejectedMessage) });
      refreshAttachList();
      paintStatus();
      return;
    }
    if (input.matches("[data-bs-upload]")) {
      const cylinder = Number(input.dataset.bsUpload);
      const files = Array.from(input.files ?? []);
      input.value = "";
      if (!files.length) return;
      const { accepted, rejectedMessage } = splitOversizedFiles(files, MAINTENANCE_IMAGE_SELECT_MAX_BYTES);
      if (rejectedMessage) {
        setStatus(rejectedMessage, { isError: true });
        if (!paintStatus()) renderContent();
      }
      if (accepted.length && Number.isInteger(cylinder) && state.borescope.editingInspectionId) {
        uploadBorescopeImages(state.borescope.editingInspectionId, cylinder, accepted, { keepStatus: Boolean(rejectedMessage) });
      }
    }
  }

  function bindFileInputs() {
    const content = contentEl();
    if (!content) return;
    content.querySelectorAll("[data-mx-attach-input], [data-bs-upload]").forEach((input) => {
      input.addEventListener("change", onFileInputChange);
    });
  }

  function wireEvents() {
    if (wired) return;
    const content = contentEl();
    if (!content) return;
    wired = true;
    content.addEventListener("click", (event) => {
      const target = (event.target as HTMLElement)?.closest?.("[data-mx-action]") as HTMLElement | null;
      if (!target) return;
      const action = target.dataset.mxAction;
      const entryId = target.dataset.mxEntryId ?? "";
      if (state.savingEntry || state.savingBaseline || state.borescope.saving) return;
      if (action === "system-history") {
        if (state.borescope.editingInspectionId || state.borescope.creating) return;
        state.selectedEngineId = target.dataset.mxEngineId ?? "";
        state.historyKind = `system:${target.dataset.mxSystemKey}`;
        state.historyQuery = "";
        state.systemsOpen = false;
        renderContent();
        content.querySelector<HTMLElement>("[data-mx-history-kind]")?.focus();
        return;
      }
      if (action === "show-activity" || action === "show-systems" || action === "select-engine") {
        if (state.composerOpen || state.editingEntryId || state.borescope.editingInspectionId || state.borescope.creating) return;
        state.editingBaseline = false;
        if (action === "select-engine") state.selectedEngineId = target.dataset.mxEngineId ?? "";
        else state.systemsOpen = action === "show-systems";
        renderContent();
        const selector = action === "select-engine" ? `[data-mx-action="select-engine"][aria-pressed="true"]` : `[data-mx-action="${action}"]`;
        content.querySelector<HTMLElement>(selector)?.focus();
        return;
      }
      if (action?.startsWith("open-") && !["open-attachment"].includes(action)) {
        state.systemsOpen = false;
        state.compactOil = action === "open-oil-added";
        state.returnAction = action === "open-oil-added" ? action : action === "open-hours-update" ? action : "open-composer";
      }
      if (action === "edit-entry") { state.systemsOpen = false; state.compactOil = false; }

      if (action === "pick-attachment" || action === "bs-pick-images") {
        if (appReadOnlyMode()) return;
        const selector = action === "pick-attachment" ? "[data-mx-attach-input]" : "[data-bs-upload]";
        target.parentElement?.querySelector<HTMLInputElement>(selector)?.click();
      } else if (action === "save-baseline") {
        const form = content.querySelector("[data-mx-baseline-form]") as HTMLElement | null;
        if (form) saveBaseline(form);
      } else if (action === "edit-baseline") {
        state.editingBaseline = true;
        setStatus(null);
        renderContent();
      } else if (action === "cancel-baseline") {
        state.formDirty = false;
        state.editingBaseline = false;
        setStatus(null);
        content.querySelector("[data-mx-setup-panel]")?.remove();
        renderContent();
      } else if (action === "open-composer") {
        state.composerOpen = true;
        state.composerKind = "general";
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
      } else if (action === "open-hours-update") {
        state.composerOpen = true;
        state.composerKind = "hours";
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
        const composer = content.querySelector("[data-mx-entry-form]");
        composer?.scrollIntoView({ behavior: "smooth", block: "center" });
        (composer?.querySelector('[data-mx-entry="tachHours"]') as HTMLInputElement | null)?.focus();
      } else if (action === "open-compression-composer") {
        state.composerOpen = true;
        state.composerKind = "compression";
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
        const composer = content.querySelector("[data-mx-entry-form]");
        composer?.scrollIntoView({ behavior: "smooth", block: "center" });
        (composer?.querySelector('[data-mx-comp-reading="0"]') as HTMLInputElement | null)?.focus();
      } else if (action === "open-oil-change" || action === "open-oil-added") {
        state.composerOpen = true;
        state.composerKind = action === "open-oil-change" ? "oil" : "oil-added";
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
        const composer = content.querySelector("[data-mx-entry-form]");
        composer?.scrollIntoView({ behavior: "smooth", block: "center" });
        (composer?.querySelector('[data-mx-oil-detail="quartsAdded"]') as HTMLInputElement | null)?.focus();
      } else if (action === "open-plug-track" || action === "open-mag-timing" || action === "open-plug-service" || action === "open-plug-rotate" || action === "open-propeller") {
        state.composerOpen = true;
        state.composerKind = action === "open-mag-timing" ? "mag-timing" : action === "open-propeller" ? "propeller" : "spark-plug";
        state.composerPlugAction = action === "open-plug-rotate" ? "rotate" : action === "open-plug-track" ? "identify" : "clean";
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
        content.querySelector("[data-mx-entry-form]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (action === "plug-helper" && target.dataset.mxMode) {
        state.composerPlugMode = target.dataset.mxMode;
        const body = target.closest("[data-mx-plug-block]")?.querySelector("[data-mx-plug-body]") as HTMLElement | null;
        if (body) body.innerHTML = plugComposerBodyMarkup("rotate");
      } else if (action === "cancel-entry") {
        state.formDirty = false;
        state.composerOpen = false;
        state.editingEntryId = null;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
        content.querySelector<HTMLElement>(`[data-mx-action="${state.returnAction}"]`)?.focus();
      } else if (action === "edit-entry" && entryId) {
        state.editingEntryId = entryId;
        state.composerOpen = false;
        state.pendingAttachments = [];
        setStatus(null);
        renderContent();
      } else if (action === "delete-entry" && entryId) {
        deleteEntry(entryId);
      } else if (action === "restore-entry" && entryId) {
        restoreEntry(entryId);
      } else if (action === "open-attachment" && target.dataset.mxAttachmentId) {
        openAttachment(target.dataset.mxAttachmentId);
      } else if (action === "delete-attachment" && target.dataset.mxAttachmentId) {
        deleteAttachment(target.dataset.mxAttachmentId);
      } else if (action === "remove-pending-attachment") {
        const index = Number(target.dataset.mxAttachIndex);
        if (Number.isInteger(index)) state.pendingAttachments.splice(index, 1);
        refreshAttachList();
      } else if (action === "retry") {
        state.error = null;
        state.loaded = false;
        loadMaintenance();
        renderContent();
      } else if (action === "bs-new-inspection") {
        state.borescope.creating = true;
        state.borescope.createDraft = null;
        renderContent();
      } else if (action === "bs-cancel-create") {
        state.borescope.creating = false;
        renderContent();
      } else if (action === "bs-create-inspection") {
        const createRow = content.querySelector("[data-bs-create]") as HTMLElement | null;
        const inspectedOn = (createRow?.querySelector("[data-bs-create-date]") as HTMLInputElement | null)?.value ?? "";
        const engineId = (createRow?.querySelector("[data-bs-create-engine]") as HTMLSelectElement | null)?.value ?? null;
        const note = (createRow?.querySelector("[data-bs-create-note]") as HTMLInputElement | null)?.value ?? "";
        state.borescope.saving = true;
        createRow?.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement>("input, button, select").forEach(control => { control.disabled = true; });
        (async () => {
          try {
            const payload = await borescopeFetch("", { method: "POST", body: JSON.stringify({ inspectedOn, engineId, note }) });
            state.borescope.creating = false;
            state.borescope.noteDraft = null;
            state.borescope.imageDrafts = {};
            state.borescope.returnToActivity = false;
            state.borescope.editingInspectionId = payload?.inspection?.id ?? null;
            state.borescope.selectedImageId = null;
            setStatus(null);
            await refreshBorescope();
          } catch (error: any) {
            setStatus(error?.message ?? "Could not create the inspection.", { isError: true });
          }
          state.borescope.saving = false;
          renderContent();
        })();
      } else if (action === "bs-open-inspection" && target.dataset.mxInspectionId) {
        state.borescope.returnToActivity = !state.systemsOpen;
        state.systemsOpen = true;
        if (!state.expandedSystems.includes("cylinders")) state.expandedSystems.push("cylinders");
        state.borescope.noteDraft = null;
        state.borescope.imageDrafts = {};
        state.borescope.editingInspectionId = target.dataset.mxInspectionId;
        state.borescope.selectedImageId = null;
        setStatus(null);
        renderContent();
        const editor = content.querySelector<HTMLElement>("[data-bs-editor]");
        editor?.querySelector<HTMLElement>('[data-mx-action="bs-close-editor"]')?.focus();
        editor?.scrollIntoView({ block: "start", behavior: "instant" });
      } else if (action === "bs-save-inspection") {
        saveBorescopeDrafts();
      } else if (action === "bs-close-editor") {
        if (borescopeDirty() && !window.confirm("Discard unsaved inspection and image changes? Uploaded images are already saved.")) return;
        const inspectionId = state.borescope.editingInspectionId;
        state.borescope.editingInspectionId = null;
        state.borescope.selectedImageId = null;
        state.borescope.noteDraft = null;
        state.borescope.imageDrafts = {};
        if (state.borescope.returnToActivity) state.systemsOpen = false;
        renderContent();
        content.querySelector<HTMLElement>(`[data-mx-${state.systemsOpen ? "inspection-id" : "record-id"}="${state.systemsOpen ? "" : "bs:"}${inspectionId}"]${state.systemsOpen ? "" : " > summary"}`)?.focus();
      } else if (action === "bs-delete-inspection" && target.dataset.mxInspectionId) {
        if (!window.confirm(`Delete this inspection and its images?\n\n${maintenanceDeleteWarningCopy()}`)) return;
        (async () => {
          try {
            await borescopeFetch(`/${encodeURIComponent(target.dataset.mxInspectionId!)}`, { method: "DELETE" });
            state.borescope.editingInspectionId = null;
            state.borescope.selectedImageId = null;
            await refreshBorescope();
            setStatus(null);
          } catch (error: any) {
            setStatus(error?.message ?? "Could not delete the inspection.", { isError: true });
          }
          renderContent();
        })();
      } else if (action === "bs-restore-inspection" && target.dataset.mxInspectionId) {
        (async () => {
          try {
            await borescopeFetch(`/${encodeURIComponent(target.dataset.mxInspectionId!)}/restore`, { method: "POST" });
            await refreshBorescope();
            setStatus(null);
          } catch (error: any) {
            setStatus(error?.message ?? "Could not restore the inspection.", { isError: true });
          }
          renderContent();
        })();
      } else if (action === "bs-open-image" && target.dataset.mxImageId) {
        if (state.borescope.editingInspectionId) {
          state.borescope.selectedImageId = target.dataset.mxImageId;
          renderContent();
        } else {
          openBorescopeImage(target.dataset.mxImageId);
        }
      } else if (action === "bs-toggle-tag") {
        target.classList.toggle("is-active");
        target.setAttribute("aria-pressed", String(target.classList.contains("is-active")));
        captureBorescopeImageDraft();
      } else if (action === "bs-save-image" && target.dataset.mxImageId) {
        saveBorescopeDrafts(target.dataset.mxImageId);
      } else if (action === "bs-delete-image" && target.dataset.mxImageId) {
        if (!window.confirm("Delete this image? This cannot be undone.")) return;
        (async () => {
          try {
            await borescopeFetch(`/images/${encodeURIComponent(target.dataset.mxImageId!)}`, { method: "DELETE" });
            delete state.borescope.imageDrafts[target.dataset.mxImageId!];
            state.borescope.selectedImageId = null;
            await refreshBorescope();
            setStatus(null);
          } catch (error: any) {
            setStatus(error?.message ?? "Could not delete the image.", { isError: true });
          }
          renderContent();
        })();
      }
    });
    content.addEventListener("click", (event) => {
      const action = (event.target as HTMLElement)?.closest<HTMLElement>("[data-mx-action]")?.dataset.mxAction;
      if (action === "edit-entry" || action?.startsWith("open-") && action !== "open-attachment") {
        const form = content.querySelector<HTMLElement>("[data-mx-entry-form]");
        const focusSelector = state.compactOil ? '[data-mx-oil-detail="quartsAdded"]'
          : action === "open-hours-update" ? `[data-mx-entry="${meterContext().field}"]`
          : action === "open-compression-composer" ? '[data-mx-comp-reading="0"]' : 'input:not([type="hidden"]), select';
        const focus = form?.querySelector<HTMLElement>(focusSelector);
        focus?.focus({ preventScroll: true });
        if (window.matchMedia("(max-width: 700px)").matches) focus?.scrollIntoView({ block: "center", behavior: "instant" });
        else form?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
    content.addEventListener("toggle", event => {
      const group = event.target as HTMLDetailsElement;
      const key = group.dataset.mxSystem;
      if (!key || !content.contains(group)) return;
      state.expandedSystems = state.expandedSystems.filter(value => value !== key);
      if (group.open) state.expandedSystems.push(key);
    }, true);
    content.addEventListener("focusin", event => {
      const target = event.target as HTMLElement;
      if (!target.closest("[data-mx-entry-form]")) return;
      const tabbar = document.querySelector<HTMLElement>("#mobileTabbar:not([hidden])");
      if (!tabbar || !tabbar.getBoundingClientRect().height) return;
      const box = target.getBoundingClientRect();
      if (box.bottom > tabbar.getBoundingClientRect().top - 12 || box.top < 76) {
        target.scrollIntoView({ block: "center", behavior: "instant" });
      }
    });
    const captureBorescopeFields = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const createRow = input.closest("[data-bs-create]");
      if (createRow) state.borescope.createDraft = {
        inspectedOn: createRow.querySelector<HTMLInputElement>("[data-bs-create-date]")?.value ?? "",
        engineId: createRow.querySelector<HTMLSelectElement>("[data-bs-create-engine]")?.value || null,
        note: createRow.querySelector<HTMLInputElement>("[data-bs-create-note]")?.value ?? "",
      };
      if (input.matches("[data-bs-inspection-note]")) { state.borescope.noteDraft = input.value; paintBorescopeDraftStatus(); }
      if (input.matches("[data-bs-detail-note], [data-bs-detail-valve]")) captureBorescopeImageDraft();
    };
    content.addEventListener("input", captureBorescopeFields);
    content.addEventListener("change", captureBorescopeFields);
    content.addEventListener("input", event => {
      const input = event.target as HTMLInputElement;
      if (input.matches("[data-mx-history-search]")) { state.historyQuery = input.value; refreshActivityRows(); }
    });
    content.addEventListener("change", event => {
      const input = event.target as HTMLSelectElement;
      if (input.matches("[data-mx-history-kind]")) { state.historyKind = input.value; refreshActivityRows(); }
    });
    content.addEventListener("submit", (event) => {
      const form = event.target as HTMLElement;
      if (form?.matches?.("[data-mx-entry-form]")) {
        event.preventDefault();
        submitEntry(form);
      } else if (form?.matches?.("[data-mx-baseline-form]")) {
        event.preventDefault();
        saveBaseline(form);
      }
    });
    // Composer controls: entry-type toggles the oil block in place (no
    // re-render, so typed values survive); the file input stages attachments
    // in state until the entry saves.
    content.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      if (input?.matches?.('[data-mx-entry="entryKind"]')) {
        const kind = normalizeMaintenanceEntryKind(input.value);
        state.composerKind = kind;
        const form = input.closest<HTMLElement>("[data-mx-entry-form]");
        if (form && !state.editingEntryId) form.dataset.mxFormKey = kind;
        const oilBlock = form?.querySelector("[data-mx-oil-block]") as HTMLElement | null;
        if (oilBlock) {
          oilBlock.hidden = kind !== "oil" && kind !== "oil-added";
          const heading = oilBlock.querySelector("[data-mx-oil-heading]");
          if (heading) heading.textContent = kind === "oil-added" ? "Oil added between changes" : "Oil change details";
          const quantityLabel = oilBlock.querySelector("[data-mx-oil-quantity-label]");
          if (quantityLabel) quantityLabel.textContent = kind === "oil-added" ? "Quarts added" : "Refill quarts";
          const quantity = oilBlock.querySelector('[data-mx-oil-detail="quartsAdded"]') as HTMLInputElement | null;
          if (quantity) { quantity.required = kind === "oil-added"; quantity.min = kind === "oil-added" ? "0.01" : "0"; }
          oilBlock.querySelectorAll<HTMLElement>("[data-mx-oil-change-only]").forEach(el => { el.hidden = kind !== "oil"; });
        }
        const compBlock = form?.querySelector("[data-mx-compression-block]") as HTMLElement | null;
        if (compBlock) compBlock.hidden = kind !== "compression";
        const magtBlock = form?.querySelector("[data-mx-magtiming-block]") as HTMLElement | null;
        if (magtBlock) magtBlock.hidden = kind !== "mag-timing";
        const plugBlock = form?.querySelector("[data-mx-plug-block]") as HTMLElement | null;
        if (plugBlock) plugBlock.hidden = kind !== "spark-plug";
        const propBlock = form?.querySelector("[data-mx-propeller-block]") as HTMLElement | null;
        if (propBlock) propBlock.hidden = kind !== "propeller";
        const engineSelect = form?.querySelector('[data-mx-entry="engineId"]') as HTMLSelectElement | null;
        if (engineSelect) {
          engineSelect.required = ["propeller", "oil", "oil-added"].includes(kind);
          engineSelect.options[0].textContent = engineSelect.required ? "Choose engine" : "Whole aircraft";
        }
        // Quick-log nicety: fill the default title for reading-style kinds,
        // but only over an empty/default value — never over user text.
        const titleInput = form?.querySelector('[data-mx-entry="title"]') as HTMLInputElement | null;
        if (titleInput) {
          const kindDefaults: Record<string, string> = {
            hours: MAINTENANCE_ENTRY_KIND_LABELS.hours,
            oil: MAINTENANCE_ENTRY_KIND_LABELS.oil,
            "oil-added": MAINTENANCE_ENTRY_KIND_LABELS["oil-added"],
            compression: MAINTENANCE_ENTRY_KIND_LABELS.compression,
            "mag-timing": MAINTENANCE_ENTRY_KIND_LABELS["mag-timing"],
            "spark-plug": MAINTENANCE_ENTRY_KIND_LABELS["spark-plug"],
            propeller: MAINTENANCE_ENTRY_KIND_LABELS.propeller,
          };
          const defaults = Object.values(kindDefaults);
          const kindDefault = kindDefaults[kind] ?? null;
          if (kindDefault && titleInput.value.trim() === "") {
            titleInput.value = kindDefault;
          } else if (defaults.includes(titleInput.value) && titleInput.value !== kindDefault) {
            titleInput.value = kindDefault ?? "";
          }
        }
        return;
      }
      if (input?.matches?.("[data-mx-plug-action]")) {
        state.composerPlugAction = input.value;
        const body = input.closest("[data-mx-plug-block]")?.querySelector("[data-mx-plug-body]") as HTMLElement | null;
        if (body) body.innerHTML = plugComposerBodyMarkup(input.value);
        return;
      }
      if (input?.matches?.("[data-mx-plug-after]")) {
        // Manual placement: re-render from the live selections so the plug chosen
        // here drops out of the other menus (and any it freed reappears).
        const body = input.closest("[data-mx-plug-body]") as HTMLElement | null;
        if (body) {
          const live: Record<string, string | null> = {};
          body.querySelectorAll("[data-mx-plug-after]").forEach((select) => {
            const position = (select as HTMLSelectElement).dataset.mxPlugAfter;
            if (position) live[position] = (select as HTMLSelectElement).value || null;
          });
          body.innerHTML = manualPlugBodyMarkup(live, composerEngineContext().cylinders);
        }
        return;
      }
      if (input?.matches?.('[data-mx-entry="engineId"]')) {
        // Twins: the plug body composes against the selected engine's board.
        const body = input.closest("[data-mx-entry-form]")?.querySelector("[data-mx-plug-body]") as HTMLElement | null;
        if (body) body.innerHTML = plugComposerBodyMarkup(state.composerPlugAction);
        return;
      }
    });
    // Meter-triangle assist: reading now = reading at overhaul + time since
    // overhaul. When two corners are filled in, complete the blank third —
    // never overwriting anything the user typed. Single-engine only: on a
    // twin the aircraft-level tach can't be derived from one engine's times.
    content.addEventListener("change", (event) => {
      const input = event.target as HTMLInputElement;
      const form = input?.closest?.("[data-mx-baseline-form]") as HTMLElement | null;
      if (!form || draftBaseline().engines.length !== 1) return;
      const triangles = [
        { now: '[data-mx-hours="tachHours"]', atOverhaul: '[data-mx-eng="0:tachAtOverhaulHours"]' },
        { now: '[data-mx-hours="ttafHours"]', atOverhaul: '[data-mx-eng="0:ttafAtOverhaulHours"]' },
      ];
      const sinceInput = form.querySelector('[data-mx-eng="0:tsmohHours"]') as HTMLInputElement | null;
      for (const triangle of triangles) {
        const nowInput = form.querySelector(triangle.now) as HTMLInputElement | null;
        const atOverhaulInput = form.querySelector(triangle.atOverhaul) as HTMLInputElement | null;
        if (!nowInput || !atOverhaulInput || !sinceInput) continue;
        const derived = maintenanceHourTriangle({
          nowHours: nowInput.value,
          atOverhaulHours: atOverhaulInput.value,
          sinceHours: sinceInput.value,
        });
        if (!derived) continue;
        const target = derived.field === "now" ? nowInput : derived.field === "atOverhaul" ? atOverhaulInput : sinceInput;
        if (target.value.trim() === "" && target !== input) target.value = String(derived.value);
      }
    });
    for (const eventName of ["input", "change"]) content.addEventListener(eventName, event => {
      if ((event.target as HTMLElement)?.closest("[data-mx-entry-form], [data-mx-baseline-form]")) state.formDirty = true;
      const form = (event.target as HTMLElement)?.closest<HTMLElement>("[data-mx-baseline-form]");
      if (form) refreshSetupReadiness(form);
    });
  }

  function confirmMaintenanceAircraftChange(nextAircraft: string): boolean {
    if (!state.aircraft || nextAircraft === state.aircraft) return true;
    if (state.savingEntry || state.savingBaseline || state.borescope.saving || state.activeOperations) {
      setStatus("Wait for the Maintenance save or upload to finish before switching aircraft.", { isError: true });
      paintStatus();
      return false;
    }
    const dirty = state.formDirty || state.pendingAttachments.length > 0 || borescopeDirty()
      || state.borescope.creating && state.borescope.createDraft !== null;
    if (dirty && !window.confirm("Discard unsaved Maintenance edits and switch aircraft? Uploaded files and previously saved records will remain saved.")) return false;
    state.formDirty = false;
    state.composerOpen = false;
    state.editingEntryId = null;
    state.pendingAttachments = [];
    state.borescope = freshState().borescope;
    return true;
  }

  return { renderAircraftMaintenancePanel, confirmMaintenanceAircraftChange };
}
